<template>
  <div class="student-dashboard">
    <v-container :class="$vuetify.display.mobile ? 'py-1 px-1' : 'py-6'" fluid>
      <!-- Welcome Section - Hide on Mobile -->
      <v-row v-if="!$vuetify.display.mobile" class="mb-4">
        <v-col cols="12">
          <v-card class="welcome-card pa-4" elevation="6">
            <v-row align="center">
              <v-col cols="12" md="8">
                <h1 class="text-h4 mb-2 text-white">
                  {{ $t('student.dashboard.welcome') }}, {{ authStore.user?.fio }}!
                </h1>
                <p class="text-h6 text-white opacity-90">
                  {{ $t('student.dashboard.subtitle') }}
                </p>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-avatar size="100" class="profile-avatar">
                  <v-icon size="50" color="white">mdi-account-circle</v-icon>
                </v-avatar>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- Schedule (inline) -->
      <StudentScheduleView />

      <!-- LIMITED_STUDENT Restriction Alert -->
      <v-row v-if="isLimitedStudent && showRestrictionAlert" class="mb-6">
        <v-col cols="12">
          <v-alert
            type="warning"
            variant="tonal"
            prominent
            closable
            class="restriction-alert"
            @click:close="dismissRestrictionAlert"
          >
            <template #prepend>
              <v-icon icon="mdi-account-alert" size="large" />
            </template>
            <div class="d-flex justify-space-between align-center">
              <div>
                <h3 class="text-h6 mb-2">{{ $t('student.restrictions.title') }}</h3>
                <p class="mb-2">{{ $t('student.restrictions.message') }}</p>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  {{ $t('student.restrictions.contactAdmin') }}
                </p>
              </div>
              <v-btn
                color="warning"
                variant="outlined"
                @click="contactAdmin"
              >
                {{ $t('student.restrictions.contactButton') }}
              </v-btn>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Minimal Overview -->
      <v-row v-if="false" class="mb-2">
        <v-col cols="12">
          <v-card class="overview-card" elevation="4">
            <v-card-title class="d-flex align-center py-3">
              <v-icon class="me-2" color="primary">mdi-view-dashboard</v-icon>
              {{ $t('student.progress.overall') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div class="d-flex flex-wrap ga-2 mb-4">
                <v-chip size="small" color="primary" variant="tonal">{{ $t('student.progress.overall') }}: {{ overallProgress }}%</v-chip>
                <v-chip v-if="currentRound" size="small" color="info" variant="tonal">{{ $t('adminUsers.rounds.round') }} {{ currentRound }}</v-chip>
                <v-chip v-if="ranking.myRank" size="small" :color="ranking.myRank <= 5 ? 'amber' : 'primary'" variant="tonal">{{ $t('student.progress.ranking') }}: {{ ranking.myRank }}/{{ ranking.totalStudents }}</v-chip>
                <v-chip v-if="examCountdown.isSet" size="small" :color="examCountdown.isPast ? 'error' : 'success'" variant="tonal">
                  {{ examCountdown.daysLeft }} {{ examCountdown.isPast ? $t('student.progress.daysPassed') : $t('student.progress.daysLeft') }}
                </v-chip>
              </div>

              <div class="overview-grid">
                <div class="overview-metric">
                  <v-icon color="primary" size="20">mdi-test-tube</v-icon>
                  <div class="overview-value">{{ stats.totalTests }}</div>
                  <div class="overview-label">{{ $t('student.dashboard.testsCompleted') }}</div>
                </div>
                <div class="overview-metric">
                  <v-icon color="success" size="20">mdi-chart-line</v-icon>
                  <div class="overview-value">{{ stats.averageScore }}%</div>
                  <div class="overview-label">{{ $t('student.dashboard.averageScore') }}</div>
                </div>
                <div class="overview-metric">
                  <v-icon color="warning" size="20">mdi-calendar-check</v-icon>
                  <div class="overview-value">{{ stats.lessonsThisWeek }}</div>
                  <div class="overview-label">{{ $t('student.dashboard.lessonsThisWeek') }}</div>
                </div>
                <div class="overview-metric">
                  <v-icon color="info" size="20">mdi-clock-outline</v-icon>
                  <div class="overview-value">{{ stats.studyHours }}</div>
                  <div class="overview-label">{{ $t('student.dashboard.studyHours') }}</div>
                </div>
              </div>

              <v-row class="mt-2">
                <v-col cols="12" md="8">
                  <div class="compact-panel h-100">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <span class="text-body-1 font-weight-medium">{{ $t('student.progress.overall') }}</span>
                      <span class="text-h6 font-weight-bold">{{ overallProgress }}%</span>
                    </div>
                    <v-progress-linear :model-value="overallProgress" height="10" rounded color="primary" />
                    <div class="d-flex flex-wrap justify-space-between text-caption text-medium-emphasis mt-2 ga-2">
                      <span>{{ $t('student.progress.theoryProgress') }}: {{ theoryProgress }}%</span>
                      <span>{{ $t('student.progress.courseProgress') }}: {{ courseProgress.overallProgress }}%</span>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="compact-panel h-100">
                    <div class="text-body-1 font-weight-medium mb-2">{{ $t('student.dashboard.nextLesson') }}</div>
                    <template v-if="nextLesson">
                      <div class="text-body-2">{{ formatDate(nextLesson.date) }}</div>
                      <div class="text-caption text-medium-emphasis">{{ nextLesson.timeSlot }}</div>
                      <div class="text-caption text-medium-emphasis">{{ nextLesson.instructor?.fio }}</div>
                    </template>
                    <template v-else>
                      <div class="text-body-2 text-medium-emphasis">{{ $t('student.dashboard.noUpcomingLessons') }}</div>
                    </template>
                    <v-divider class="my-3" />
                    <div class="d-flex justify-space-between align-center">
                      <div>
                        <div class="text-caption text-medium-emphasis">{{ $t('student.progress.currentStreak') }}</div>
                        <div class="text-h6 font-weight-bold text-orange">{{ progressStreak.currentStreak }}</div>
                      </div>
                      <div class="text-right">
                        <div class="text-caption text-medium-emphasis">{{ $t('student.progress.longestStreak') }}</div>
                        <div class="text-h6 font-weight-bold">{{ progressStreak.longestStreak }}</div>
                      </div>
                    </div>

                    <v-divider class="my-3" />

                    <template v-if="examCountdown.isSet">
                      <div class="text-caption text-medium-emphasis">{{ $t('student.progress.examCountdown') }}</div>
                      <div class="text-body-2">{{ formatFullDate(examCountdown.examDate) }}</div>
                      <v-btn variant="text" size="small" color="error" class="mt-1 px-0" @click="clearExamDate">
                        {{ $t('student.progress.remove') }}
                      </v-btn>
                    </template>
                    <template v-else>
                      <div class="text-caption text-medium-emphasis mb-2">{{ $t('student.progress.setExamDate') }}</div>
                      <v-text-field
                        v-model="newExamDate"
                        type="date"
                        :label="$t('student.progress.examDate')"
                        variant="outlined"
                        density="compact"
                        hide-details
                      />
                      <v-btn color="primary" variant="text" size="small" class="mt-2 px-0" :disabled="!newExamDate" @click="setExamDate">
                        {{ $t('student.progress.save') }}
                      </v-btn>
                    </template>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <div class="d-flex flex-wrap ga-2">
                <v-btn color="primary" variant="flat" :disabled="isLimitedStudent" @click="$router.push('/student/tests')">
                  {{ $t('nav.tests') }}
                </v-btn>
                <v-btn color="primary" variant="outlined" @click="$router.push('/student/schedule')">
                  {{ $t('nav.schedule') }}
                </v-btn>
                <v-btn color="primary" variant="outlined" @click="$router.push('/student/instructors')">
                  {{ $t('nav.instructors') }}
                </v-btn>
                <v-btn color="primary" variant="text" @click="$router.push('/student/profile')">
                  {{ $t('nav.profile') }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Ranking - TOP 5 + My Position -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="4">
            <v-card-title class="d-flex align-center py-3">
              <v-icon class="me-2" color="amber">mdi-podium</v-icon>
              {{ $t('student.progress.ranking') }}
              <v-spacer />
              <!-- Show ranking chip when myRank is defined (including 0) -->
              <v-chip v-if="ranking.myRank !== null && ranking.myRank !== undefined" size="small" variant="tonal" :color="ranking.myRank <= 5 ? 'amber' : 'primary'">
                {{ ranking.myRank }}/{{ ranking.totalStudents }}
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-0" v-if="ranking.topStudents.length > 0">
              <div v-if="$vuetify.display.smAndDown" class="ranking-mobile-list">
                <div
                  v-for="student in rankingTop5"
                  :key="`mobile-${student.rank}`"
                  class="ranking-mobile-item"
                  :class="{ 'is-self': student.isMe }"
                >
                  <div class="ranking-mobile-head">
                    <div class="ranking-rank-badge" :class="{ 'is-self': student.isMe }">
                      <v-icon v-if="student.rank === 1" color="amber" size="20">mdi-crown</v-icon>
                      <v-icon v-else-if="student.rank === 2" color="grey" size="20">mdi-medal</v-icon>
                      <v-icon v-else-if="student.rank === 3" color="brown" size="20">mdi-medal</v-icon>
                      <span v-else>{{ student.rank }}</span>
                    </div>
                    <div class="ranking-student-card ranking-student-card-mobile" :class="{ 'is-self': student.isMe }">
                      <div class="ranking-student-name" :class="{ 'text-primary': student.isMe }">
                        {{ student.name }}
                      </div>
                    </div>
                  </div>

                  <div class="ranking-mobile-stack">
                    <div class="ranking-metric-card ranking-metric-card-mobile" :class="{ 'is-self': student.isMe }">
                      <div class="ranking-metric-header">
                        <span class="ranking-card-label">{{ $t('student.progress.correctAnswerRate') }}</span>
                        <span
                          class="ranking-progress-percent ranking-metric-badge"
                          :class="`is-${getRankingProgressState(student.accuracy)}`"
                        >
                          {{ normalizeProgressValue(student.accuracy) }}%
                        </span>
                      </div>
                      <div class="ranking-progress-track is-accuracy" aria-hidden="true">
                        <div
                          class="ranking-progress-fill is-accuracy"
                          :style="{ width: `${normalizeProgressValue(student.accuracy)}%` }"
                        ></div>
                      </div>
                    </div>

                    <div class="ranking-progress-card ranking-progress-card-mobile" :class="{ 'is-self': student.isMe }">
                      <div class="ranking-progress-header">
                        <span class="ranking-card-label">{{ $t('adminUsers.rounds.progress') }}</span>
                        <span
                          class="ranking-progress-percent"
                          :class="`is-${getRankingProgressState(student.progress)}`"
                        >
                          {{ normalizeProgressValue(student.progress) }}%
                        </span>
                      </div>
                      <div class="ranking-progress-count">{{ student.answered }}/{{ student.totalTests }}</div>
                      <div class="ranking-progress-track" aria-hidden="true">
                        <div
                          class="ranking-progress-fill"
                          :style="{ width: `${normalizeProgressValue(student.progress)}%` }"
                        ></div>
                      </div>
                      <div class="ranking-progress-footer">
                        <span class="ranking-progress-pill">
                          {{ $t('adminUsers.rounds.round') }} {{ student.completedRounds || 0 }}
                        </span>
                        <span class="ranking-progress-pill is-rank">
                          {{ formatRankScore(student.rankScore) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <template v-if="myRankingRow">
                  <div class="ranking-mobile-divider">{{ $t('student.progress.yourRank') }}</div>
                  <div class="ranking-mobile-item is-self">
                    <div class="ranking-mobile-head">
                      <div class="ranking-rank-badge is-self">
                        <span>{{ myRankingRow.rank }}</span>
                      </div>
                      <div class="ranking-student-card ranking-student-card-mobile is-self">
                        <div class="ranking-student-name text-primary">
                          {{ myRankingRow.name }}
                        </div>
                      </div>
                    </div>

                    <div class="ranking-mobile-stack">
                      <div class="ranking-metric-card ranking-metric-card-mobile is-self">
                        <div class="ranking-metric-header">
                          <span class="ranking-card-label">{{ $t('student.progress.correctAnswerRate') }}</span>
                          <span
                            class="ranking-progress-percent ranking-metric-badge"
                            :class="`is-${getRankingProgressState(myRankingRow.accuracy)}`"
                          >
                            {{ normalizeProgressValue(myRankingRow.accuracy) }}%
                          </span>
                        </div>
                        <div class="ranking-progress-track is-accuracy" aria-hidden="true">
                          <div
                            class="ranking-progress-fill is-accuracy"
                            :style="{ width: `${normalizeProgressValue(myRankingRow.accuracy)}%` }"
                          ></div>
                        </div>
                      </div>

                      <div class="ranking-progress-card ranking-progress-card-mobile is-self">
                        <div class="ranking-progress-header">
                          <span class="ranking-card-label">{{ $t('adminUsers.rounds.progress') }}</span>
                          <span
                            class="ranking-progress-percent"
                            :class="`is-${getRankingProgressState(myRankingRow.progress)}`"
                          >
                            {{ normalizeProgressValue(myRankingRow.progress) }}%
                          </span>
                        </div>
                        <div class="ranking-progress-count">{{ myRankingRow.answered }}/{{ myRankingRow.totalTests }}</div>
                        <div class="ranking-progress-track" aria-hidden="true">
                          <div
                            class="ranking-progress-fill"
                            :style="{ width: `${normalizeProgressValue(myRankingRow.progress)}%` }"
                          ></div>
                        </div>
                        <div class="ranking-progress-footer">
                          <span class="ranking-progress-pill">
                            {{ $t('adminUsers.rounds.round') }} {{ myRankingRow.completedRounds || 0 }}
                          </span>
                          <span class="ranking-progress-pill is-rank">
                            {{ formatRankScore(myRankingRow.rankScore) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <v-table v-else density="comfortable" class="ranking-table ranking-table-desktop">
                <colgroup>
                  <col class="ranking-col-rank">
                  <col class="ranking-col-student">
                  <col class="ranking-col-accuracy">
                  <col class="ranking-col-progress">
                </colgroup>
                <thead>
                  <tr>
                    <th class="text-center">#</th>
                    <th class="text-center">{{ $t('student.progress.student') }}</th>
                    <th class="text-center">{{ $t('student.progress.correctAnswerRate') }}</th>
                    <th class="text-center">{{ $t('adminUsers.rounds.progress') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="student in rankingTop5" :key="student.rank" :class="{ 'bg-amber-lighten-4': student.isMe }">
                    <td class="text-center ranking-rank-cell">
                      <div class="ranking-rank-badge" :class="{ 'is-self': student.isMe }">
                        <v-icon v-if="student.rank === 1" color="amber" size="20">mdi-crown</v-icon>
                        <v-icon v-else-if="student.rank === 2" color="grey" size="20">mdi-medal</v-icon>
                        <v-icon v-else-if="student.rank === 3" color="brown" size="20">mdi-medal</v-icon>
                        <span v-else>{{ student.rank }}</span>
                      </div>
                    </td>
                    <td class="text-center">
                      <div class="ranking-student-card" :class="{ 'is-self': student.isMe }">
                        <div class="ranking-student-name" :class="{ 'text-primary': student.isMe }">
                          {{ student.name }}
                        </div>
                      </div>
                    </td>
                    <td class="text-center">
                      <div class="ranking-metric-card" :class="{ 'is-self': student.isMe }">
                        <div class="ranking-metric-header">
                          <span class="ranking-card-label">{{ $t('student.progress.correctAnswerRate') }}</span>
                          <span
                            class="ranking-progress-percent ranking-metric-badge"
                            :class="`is-${getRankingProgressState(student.accuracy)}`"
                          >
                            {{ normalizeProgressValue(student.accuracy) }}%
                          </span>
                        </div>
                        <div class="ranking-progress-track is-accuracy" aria-hidden="true">
                          <div
                            class="ranking-progress-fill is-accuracy"
                            :style="{ width: `${normalizeProgressValue(student.accuracy)}%` }"
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td class="text-center">
                      <div class="ranking-progress-card" :class="{ 'is-self': student.isMe }">
                        <div class="ranking-progress-header">
                          <span class="ranking-card-label">{{ $t('adminUsers.rounds.progress') }}</span>
                          <span
                            class="ranking-progress-percent"
                            :class="`is-${getRankingProgressState(student.progress)}`"
                          >
                            {{ normalizeProgressValue(student.progress) }}%
                          </span>
                        </div>
                        <div class="ranking-progress-count">{{ student.answered }}/{{ student.totalTests }}</div>
                        <div class="ranking-progress-track" aria-hidden="true">
                          <div
                            class="ranking-progress-fill"
                            :style="{ width: `${normalizeProgressValue(student.progress)}%` }"
                          ></div>
                        </div>
                        <div class="ranking-progress-footer">
                          <span class="ranking-progress-pill">
                            {{ $t('adminUsers.rounds.round') }} {{ student.completedRounds || 0 }}
                          </span>
                          <span class="ranking-progress-pill is-rank">
                            {{ formatRankScore(student.rankScore) }}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>

                  <template v-if="myRankingRow">
                    <tr>
                      <td colspan="4" class="text-center pa-0">
                        <v-divider />
                        <div class="ranking-separator-label">{{ $t('student.progress.yourRank') }}</div>
                        <v-divider />
                      </td>
                    </tr>
                    <tr class="bg-primary-lighten-5">
                      <td class="text-center ranking-rank-cell">
                        <div class="ranking-rank-badge is-self">
                          <span>{{ myRankingRow.rank }}</span>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="ranking-student-card is-self">
                          <div class="ranking-student-name text-primary">
                            {{ myRankingRow.name }}
                          </div>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="ranking-metric-card is-self">
                          <div class="ranking-metric-header">
                            <span class="ranking-card-label">{{ $t('student.progress.correctAnswerRate') }}</span>
                            <span
                              class="ranking-progress-percent ranking-metric-badge"
                              :class="`is-${getRankingProgressState(myRankingRow.accuracy)}`"
                            >
                              {{ normalizeProgressValue(myRankingRow.accuracy) }}%
                            </span>
                          </div>
                          <div class="ranking-progress-track is-accuracy" aria-hidden="true">
                            <div
                              class="ranking-progress-fill is-accuracy"
                              :style="{ width: `${normalizeProgressValue(myRankingRow.accuracy)}%` }"
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="ranking-progress-card is-self">
                          <div class="ranking-progress-header">
                            <span class="ranking-card-label">{{ $t('adminUsers.rounds.progress') }}</span>
                            <span
                              class="ranking-progress-percent"
                              :class="`is-${getRankingProgressState(myRankingRow.progress)}`"
                            >
                              {{ normalizeProgressValue(myRankingRow.progress) }}%
                            </span>
                          </div>
                          <div class="ranking-progress-count">{{ myRankingRow.answered }}/{{ myRankingRow.totalTests }}</div>
                          <div class="ranking-progress-track" aria-hidden="true">
                            <div
                              class="ranking-progress-fill"
                              :style="{ width: `${normalizeProgressValue(myRankingRow.progress)}%` }"
                            ></div>
                          </div>
                          <div class="ranking-progress-footer">
                            <span class="ranking-progress-pill">
                              {{ $t('adminUsers.rounds.round') }} {{ myRankingRow.completedRounds || 0 }}
                            </span>
                            <span class="ranking-progress-pill is-rank">
                              {{ formatRankScore(myRankingRow.rankScore) }}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </v-table>
            </v-card-text>
            <v-card-text v-else class="text-center py-4">
              <v-icon size="40" color="grey-lighten-2" class="mb-2">mdi-podium</v-icon>
              <p class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noRankingData') }}</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="4">
            <v-card-title class="d-flex align-center py-3">
              <v-icon class="me-2" color="error">mdi-clipboard-alert-outline</v-icon>
              {{ $t('student.progress.mistakesWork') }}
            </v-card-title>
            <v-divider />
            <v-card-text class="d-flex flex-column flex-md-row align-start align-md-center justify-space-between ga-4">
              <div>
                <div class="text-overline text-warning font-weight-bold mb-1">
                  {{ $t('student.progress.yourMistakes') }}
                </div>
                <div class="text-body-1 font-weight-medium mb-1">
                  {{ $t('student.progress.incorrectQuestionsCount') }}: {{ incorrectQuestions.count }}
                </div>
                <div class="text-body-2 text-medium-emphasis">
                  {{
                    incorrectQuestions.count > 0
                      ? $t('student.progress.mistakesWorkDesc')
                      : $t('student.progress.noIncorrectQuestions')
                  }}
                </div>
              </div>
              <v-btn
                color="error"
                variant="flat"
                :disabled="incorrectQuestions.count === 0 || isLimitedStudent"
                @click="startIncorrectQuestionsReview"
              >
                <v-icon start>mdi-refresh</v-icon>
                {{ $t('student.progress.retryIncorrectQuestions') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="false" class="mb-6">
        <v-col cols="12">
          <v-card elevation="4">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2">mdi-target</v-icon>
              {{ $t('student.progress.studyPlan') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="minimal-section h-100">
                    <div class="section-label">{{ $t('student.progress.weakTopics') }}</div>
                    <template v-if="weakTopics.length > 0">
                      <div v-for="topic in weakTopics.slice(0, 4)" :key="topic.category" class="mb-4">
                        <div class="d-flex justify-space-between align-center mb-1">
                          <span class="text-body-2">{{ formatCategoryName(topic.category) }}</span>
                          <v-chip size="x-small" :color="topic.errorRate > 50 ? 'error' : topic.errorRate > 30 ? 'warning' : 'success'" variant="tonal">
                            {{ topic.errorRate }}% {{ $t('student.progress.errorRate') }}
                          </v-chip>
                        </div>
                        <v-progress-linear :model-value="topic.successRate" height="6" rounded :color="topic.successRate >= 70 ? 'success' : topic.successRate >= 50 ? 'warning' : 'error'" />
                        <div class="text-caption text-medium-emphasis mt-1">
                          {{ topic.correct }} / {{ topic.total }} {{ $t('student.progress.answers') }}
                        </div>
                      </div>
                    </template>
                    <div v-else class="text-center py-6">
                      <v-icon size="40" color="grey-lighten-2" class="mb-2">mdi-shield-check</v-icon>
                      <div class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noWeakTopics') }}</div>
                    </div>

                    <v-divider class="my-4" />

                    <div class="section-label">{{ $t('student.progress.recommendedTests') }}</div>
                    <template v-if="weakCategories.length > 0">
                      <v-list density="compact" class="py-0">
                        <v-list-item v-for="(cat, idx) in weakCategories.slice(0, 4)" :key="idx" class="px-0" @click="startCategoryTest(cat.category)">
                          <template #prepend>
                            <v-icon size="small" :color="cat.errorRate > 50 ? 'error' : cat.errorRate > 30 ? 'warning' : 'info'">mdi-play-circle-outline</v-icon>
                          </template>
                          <v-list-item-title class="text-body-2">{{ formatCategoryName(cat.category) }}</v-list-item-title>
                          <v-list-item-subtitle class="text-caption">
                            {{ cat.errorCount }} {{ $t('student.progress.errors') }} / {{ cat.errorRate }}%
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                      <v-btn color="primary" variant="outlined" size="small" class="mt-3" @click="startRecommendedTest">
                        {{ $t('student.progress.startRecommended') }}
                      </v-btn>
                    </template>
                    <div v-else class="text-body-2 text-medium-emphasis">
                      {{ $t('student.progress.noRecommendations') }}
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="minimal-section h-100">
                    <div class="section-label">{{ $t('student.progress.recommendations') }}</div>
                    <v-list density="compact" class="py-0">
                      <v-list-item v-for="rec in progressRecommendations.slice(0, 3)" :key="rec.id" class="px-0">
                        <template #prepend>
                          <v-icon :icon="rec.icon" :color="rec.color" size="small" />
                        </template>
                        <v-list-item-title class="text-body-2">{{ rec.title }}</v-list-item-title>
                        <v-list-item-subtitle>{{ rec.description }}</v-list-item-subtitle>
                        <template #append>
                          <v-btn :color="rec.color" variant="text" size="small" @click="handleRecommendation(rec)">{{ rec.action }}</v-btn>
                        </template>
                      </v-list-item>
                    </v-list>

                    <v-divider class="my-4" />

                    <div class="section-label">{{ $t('student.progress.goals') }}</div>
                    <div v-for="goal in progressGoals.slice(0, 3)" :key="goal.id" class="mb-4">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-body-2">{{ goal.title }}</span>
                        <v-chip size="x-small" :color="goal.color" variant="tonal">{{ goal.progress }}%</v-chip>
                      </div>
                      <v-progress-linear :model-value="goal.progress" height="6" rounded :color="goal.color" />
                      <div class="text-caption text-medium-emphasis mt-1">{{ goal.description }}</div>
                    </div>

                    <v-divider class="my-4" />

                    <div class="section-label">{{ $t('student.dashboard.tips') }}</div>
                    <v-list density="compact" class="py-0">
                      <v-list-item v-for="(tip, index) in tips.slice(0, 3)" :key="index" class="px-0">
                        <template #prepend>
                          <v-icon icon="mdi-check-circle" color="success" size="small" />
                        </template>
                        <v-list-item-title class="text-body-2">{{ tip }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="false" class="mb-6">
        <v-col cols="12">
          <v-card elevation="4">
            <v-card-text>
              <div class="minimal-section mb-4">
                <div class="section-label">{{ $t('student.progress.theoryProgress') }}</div>
                <div v-if="theoryCategories.length > 0">
                  <div v-for="cat in theoryCategories.slice(0, 5)" :key="cat.name" class="mb-3">
                    <div class="d-flex justify-space-between align-center mb-1">
                      <span class="text-body-2">{{ cat.name }}</span>
                      <v-chip size="x-small" :color="getProgressColor(cat.progress)" variant="tonal">{{ cat.progress }}%</v-chip>
                    </div>
                    <v-progress-linear :model-value="cat.progress" height="4" rounded :color="getProgressColor(cat.progress)" />
                  </div>
                </div>
                <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noTests') }}</div>
              </div>

              <div class="minimal-section mb-4">
                <div class="section-label">{{ $t('adminUsers.rounds.title') }}</div>
                <v-table v-if="rounds.length > 0" density="compact">
                  <thead>
                    <tr>
                      <th>{{ $t('adminUsers.rounds.round') }}</th>
                      <th>{{ $t('student.progress.correctAnswerRate') }}</th>
                      <th>{{ $t('adminUsers.rounds.progress') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in rounds.slice(0, 5)" :key="r.round">
                      <td>{{ r.round }}</td>
                      <td>{{ r.percentage }}%</td>
                      <td>{{ r.answered }}/{{ r.totalTests }}</td>
                    </tr>
                  </tbody>
                </v-table>
                <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.dashboard.noRoundsData') }}</div>
              </div>

              <div class="minimal-section mb-4">
                <div class="section-label">{{ $t('student.dashboard.recentTests') }}</div>
                <v-skeleton-loader v-if="loading" type="table-row@3" />
                <v-table v-else-if="recentTests.length > 0" density="compact">
                  <thead>
                    <tr>
                      <th>{{ $t('test.mode') }}</th>
                      <th>{{ $t('test.score') }}</th>
                      <th>{{ $t('test.date') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="test in recentTests.slice(0, 5)" :key="test.id" @click="viewTestDetails(test)" class="cursor-pointer">
                      <td>{{ getModeText(test.mode) }}</td>
                      <td>
                        <v-chip size="x-small" :color="test.score >= 80 ? 'success' : 'error'" variant="tonal">
                          {{ test.score }}%
                        </v-chip>
                      </td>
                      <td>{{ formatShortDate(test.createdAt) }}</td>
                    </tr>
                  </tbody>
                </v-table>
                <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.dashboard.noTests') }}</div>
              </div>

              <div class="minimal-section">
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="section-label mb-0">{{ $t('student.progress.scoreDynamics') }}</div>
                  <v-chip
                    v-if="scoreHistory.trend"
                    :color="scoreHistory.trend === 'up' ? 'success' : scoreHistory.trend === 'down' ? 'error' : 'grey'"
                    size="x-small"
                    variant="tonal"
                  >
                    {{ scoreHistory.trend === 'up' ? $t('student.progress.trendUp') : scoreHistory.trend === 'down' ? $t('student.progress.trendDown') : $t('student.progress.trendStable') }}
                  </v-chip>
                </div>
                <div v-if="scoreHistory.data.length > 1" style="height: 260px; position: relative;">
                  <Line :data="lineChartData" :options="lineChartOptions" />
                </div>
                <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.progress.chartEmpty') }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="false" class="mb-6">
        <v-col cols="12">
          <v-card elevation="4">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2">mdi-target</v-icon>
              {{ $t('student.progress.studyPlan') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="minimal-section h-100">
                    <div class="section-label">{{ $t('student.progress.weakTopics') }}</div>
                    <template v-if="weakTopics.length > 0">
                      <div v-for="topic in weakTopics.slice(0, 4)" :key="topic.category" class="mb-4">
                        <div class="d-flex justify-space-between align-center mb-1">
                          <span class="text-body-2">{{ formatCategoryName(topic.category) }}</span>
                          <v-chip size="x-small" :color="topic.errorRate > 50 ? 'error' : topic.errorRate > 30 ? 'warning' : 'success'" variant="tonal">
                            {{ topic.errorRate }}%
                          </v-chip>
                        </div>
                        <v-progress-linear :model-value="topic.successRate" height="6" rounded :color="topic.successRate >= 70 ? 'success' : topic.successRate >= 50 ? 'warning' : 'error'" />
                      </div>
                    </template>
                    <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noWeakTopics') }}</div>

                    <v-divider class="my-4" />

                    <div class="section-label">{{ $t('student.progress.recommendedTests') }}</div>
                    <template v-if="weakCategories.length > 0">
                      <v-list density="compact" class="py-0">
                        <v-list-item v-for="(cat, idx) in weakCategories.slice(0, 4)" :key="idx" class="px-0" @click="startCategoryTest(cat.category)">
                          <template #prepend>
                            <v-icon size="small" color="primary">mdi-play-circle-outline</v-icon>
                          </template>
                          <v-list-item-title class="text-body-2">{{ formatCategoryName(cat.category) }}</v-list-item-title>
                          <v-list-item-subtitle>{{ cat.errorCount }} {{ $t('student.progress.errors') }}</v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                      <v-btn color="primary" variant="outlined" size="small" class="mt-3" @click="startRecommendedTest">
                        {{ $t('student.progress.startRecommended') }}
                      </v-btn>
                    </template>
                    <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noRecommendations') }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="minimal-section h-100">
                    <div class="section-label">{{ $t('student.progress.recommendations') }}</div>
                    <v-list density="compact" class="py-0">
                      <v-list-item v-for="rec in progressRecommendations.slice(0, 3)" :key="rec.id" class="px-0">
                        <template #prepend>
                          <v-icon :icon="rec.icon" :color="rec.color" size="small" />
                        </template>
                        <v-list-item-title class="text-body-2">{{ rec.title }}</v-list-item-title>
                        <v-list-item-subtitle>{{ rec.description }}</v-list-item-subtitle>
                        <template #append>
                          <v-btn :color="rec.color" variant="text" size="small" @click="handleRecommendation(rec)">{{ rec.action }}</v-btn>
                        </template>
                      </v-list-item>
                    </v-list>

                    <v-divider class="my-4" />

                    <div class="section-label">{{ $t('student.progress.goals') }}</div>
                    <div v-for="goal in progressGoals.slice(0, 3)" :key="goal.id" class="mb-4">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-body-2">{{ goal.title }}</span>
                        <v-chip size="x-small" :color="goal.color" variant="tonal">{{ goal.progress }}%</v-chip>
                      </div>
                      <v-progress-linear :model-value="goal.progress" height="6" rounded :color="goal.color" />
                      <div class="text-caption text-medium-emphasis mt-1">{{ goal.description }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="false" class="mb-6">
        <v-col cols="12">
          <v-card elevation="4">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2">mdi-chart-box</v-icon>
              {{ $t('student.progress.overall') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row class="mb-2">
                <v-col cols="12" sm="6" md="3">
                  <div class="overview-metric compact">
                    <div class="overview-value">{{ theoryProgress }}%</div>
                    <div class="overview-label">{{ $t('student.progress.theoryProgress') }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="overview-metric compact">
                    <div class="overview-value">{{ practiceProgress }}%</div>
                    <div class="overview-label">{{ $t('student.progress.practiceProgress') }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="overview-metric compact">
                    <div class="overview-value">{{ courseProgress.overallProgress }}%</div>
                    <div class="overview-label">{{ $t('student.progress.courseProgress') }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="overview-metric compact">
                    <div class="overview-value">{{ badges.unlockedCount }}/{{ badges.totalCount }}</div>
                    <div class="overview-label">{{ $t('student.progress.badges') }}</div>
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" xl="7">
                  <div class="minimal-section h-100">
                    <div class="d-flex align-center justify-space-between mb-3">
                      <div class="section-label mb-0">{{ $t('student.progress.scoreDynamics') }}</div>
                      <v-chip
                        v-if="scoreHistory.trend"
                        :color="scoreHistory.trend === 'up' ? 'success' : scoreHistory.trend === 'down' ? 'error' : 'grey'"
                        size="x-small"
                        variant="tonal"
                      >
                        {{ scoreHistory.trend === 'up' ? $t('student.progress.trendUp') : scoreHistory.trend === 'down' ? $t('student.progress.trendDown') : $t('student.progress.trendStable') }}
                      </v-chip>
                    </div>
                    <div v-if="scoreHistory.data.length > 1" style="height: 260px; position: relative;">
                      <Line :data="lineChartData" :options="lineChartOptions" />
                    </div>
                    <div v-else class="text-center py-8">
                      <v-icon size="48" color="grey-lighten-2" class="mb-2">mdi-chart-line-variant</v-icon>
                      <div class="text-body-2 text-medium-emphasis">{{ $t('student.progress.chartEmpty') }}</div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" xl="5">
                  <div class="minimal-section h-100">
                    <div class="d-flex align-center justify-space-between mb-3">
                      <div class="section-label mb-0">{{ $t('student.dashboard.recentTests') }}</div>
                      <v-btn v-if="!isLimitedStudent" variant="text" color="primary" size="small" @click="$router.push('/student/tests')">
                        {{ $t('common.viewAll') }}
                      </v-btn>
                    </div>
                    <v-skeleton-loader v-if="loading" type="table-row@3" />
                    <v-table v-else-if="recentTests.length > 0" density="compact">
                      <thead>
                        <tr>
                          <th>{{ $t('test.mode') }}</th>
                          <th>{{ $t('test.score') }}</th>
                          <th>{{ $t('test.date') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="test in recentTests.slice(0, 4)" :key="test.id" @click="viewTestDetails(test)" class="cursor-pointer">
                          <td>{{ getModeText(test.mode) }}</td>
                          <td>
                            <v-chip size="x-small" :color="test.score >= 80 ? 'success' : 'error'" variant="tonal">
                              {{ test.score }}%
                            </v-chip>
                          </td>
                          <td>{{ formatShortDate(test.createdAt) }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                    <div v-else class="text-center py-8">
                      <v-icon size="40" color="grey-lighten-2" class="mb-2">mdi-test-tube-empty</v-icon>
                      <div class="text-body-2 text-medium-emphasis">{{ $t('student.dashboard.noTests') }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <v-row>
                <v-col cols="12" lg="6">
                  <div class="minimal-section h-100">
                    <div class="d-flex align-center justify-space-between mb-3">
                      <div class="section-label mb-0">{{ $t('adminUsers.rounds.title') }}</div>
                      <v-chip v-if="currentRound" size="x-small" color="info" variant="tonal">{{ $t('adminUsers.rounds.round') }} {{ currentRound }}</v-chip>
                    </div>
                    <v-table v-if="rounds.length > 0" density="compact">
                      <thead>
                        <tr>
                          <th>{{ $t('adminUsers.rounds.round') }}</th>
                      <th>{{ $t('student.progress.correctAnswerRate') }}</th>
                          <th>{{ $t('adminUsers.rounds.progress') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="r in rounds.slice(0, 4)" :key="r.round">
                          <td>{{ r.round }}</td>
                          <td>{{ r.percentage }}%</td>
                          <td>{{ r.answered }}/{{ r.totalTests }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                    <div v-else class="text-center py-8">
                      <v-icon size="40" color="grey-lighten-2" class="mb-2">mdi-chart-timeline-variant</v-icon>
                      <div class="text-body-2 text-medium-emphasis">{{ $t('student.dashboard.noRoundsData') }}</div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" lg="6">
                  <div class="minimal-section h-100">
                    <div class="section-label">{{ $t('student.progress.courseProgress') }}</div>
                    <div class="mb-3">
                      <div class="d-flex justify-space-between text-body-2 mb-1">
                        <span>{{ courseProgress.completedLessons }} / {{ courseProgress.totalLessons }} {{ $t('student.progress.lessonsCount') }}</span>
                        <span>{{ courseProgress.totalWatchedMinutes }} {{ $t('student.progress.minutesWatched') }}</span>
                      </div>
                      <v-progress-linear :model-value="courseProgress.overallProgress" height="8" rounded color="primary" />
                    </div>
                    <div v-if="courseProgress.categories.length > 0">
                      <div v-for="category in courseProgress.categories.slice(0, 4)" :key="category.id" class="mb-3">
                        <div class="d-flex justify-space-between align-center mb-1">
                          <span class="text-body-2">{{ category.title }}</span>
                          <v-chip size="x-small" :color="getProgressColor(category.progress)" variant="tonal">{{ category.progress }}%</v-chip>
                        </div>
                        <v-progress-linear :model-value="category.progress" height="4" rounded :color="getProgressColor(category.progress)" />
                      </div>
                    </div>
                    <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noVideoLessons') }}</div>

                    <v-divider class="my-4" />

                    <div class="section-label">{{ $t('student.progress.badges') }}</div>
                    <div v-if="badges.list.length > 0" class="d-flex flex-wrap ga-2">
                      <v-chip v-for="badge in badges.list.slice(0, 4)" :key="badge.key" size="small" :color="badge.unlocked ? badge.color : 'grey'" variant="tonal">
                        {{ badge.title }}
                      </v-chip>
                    </div>
                    <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noBadges') }}</div>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <v-row>
                <v-col cols="12" md="6">
                  <div class="minimal-section h-100">
                    <div class="section-label">{{ $t('student.progress.theoryProgress') }}</div>
                    <div v-if="theoryCategories.length > 0">
                      <div v-for="cat in theoryCategories.slice(0, 4)" :key="cat.name" class="mb-3">
                        <div class="d-flex justify-space-between align-center mb-1">
                          <span class="text-body-2">{{ cat.name }}</span>
                          <v-chip size="x-small" :color="getProgressColor(cat.progress)" variant="tonal">{{ cat.progress }}%</v-chip>
                        </div>
                        <v-progress-linear :model-value="cat.progress" height="4" rounded :color="getProgressColor(cat.progress)" />
                      </div>
                    </div>
                    <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noTests') }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="minimal-section h-100">
                    <div class="section-label">{{ $t('student.progress.practiceProgress') }}</div>
                    <div v-if="practiceSkills.length > 0">
                      <div v-for="skill in practiceSkills.slice(0, 4)" :key="skill.name" class="mb-3">
                        <div class="d-flex justify-space-between align-center mb-1">
                          <span class="text-body-2">{{ skill.name }}</span>
                          <v-chip size="x-small" :color="getSkillColor(skill.rating)" variant="tonal">{{ skill.rating }}/5</v-chip>
                        </div>
                        <v-progress-linear :model-value="(skill.rating / 5) * 100" height="4" rounded :color="getSkillColor(skill.rating)" />
                      </div>
                    </div>
                    <div v-else class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noLessons') }}</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Content Row -->
      <v-row v-if="false">
        <!-- Recent Tests -->
        <v-col cols="12" lg="8">
          <v-card class="mb-6" elevation="4">
            <v-card-title class="d-flex align-center">
              <v-icon icon="mdi-history" class="me-2" />
              {{ $t('student.dashboard.recentTests') }}
              <v-spacer />
              <v-btn
                v-if="!isLimitedStudent"
                variant="text"
                color="primary"
                size="small"
                @click="$router.push('/student/tests')"
              >
                {{ $t('common.viewAll') }}
              </v-btn>
              <v-btn
                v-else
                variant="text"
                color="warning"
                size="small"
                disabled
              >
                {{ $t('student.restrictions.restricted') }}
              </v-btn>
            </v-card-title>
            <v-divider />

            <!-- Loading -->
            <v-card-text v-if="loading">
              <v-skeleton-loader type="table-row@3" />
            </v-card-text>

            <!-- Tests Table -->
            <v-card-text v-else-if="recentTests.length > 0" class="pa-0">
              <v-table>
                <thead>
                  <tr>
                    <th>{{ $t('test.mode') }}</th>
                    <th>{{ $t('test.score') }}</th>
                    <th>{{ $t('test.date') }}</th>
                    <th>{{ $t('common.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="test in recentTests" :key="test.id">
                    <td>
                      <v-chip size="small" :color="getModeColor(test.mode)">
                        {{ getModeText(test.mode) }}
                      </v-chip>
                    </td>
                    <td>
                      <v-chip
                        size="small"
                        :color="test.score >= 80 ? 'success' : 'error'"
                      >
                        {{ test.score }}%
                      </v-chip>
                    </td>
                    <td>{{ formatDate(test.createdAt) }}</td>
                    <td>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="primary"
                        @click="viewTestDetails(test)"
                      >
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-card-text>

            <!-- Empty State -->
            <v-card-text v-else class="text-center pa-8">
              <v-icon icon="mdi-test-tube-empty" size="64" class="text-medium-emphasis mb-3" />
              <p class="text-medium-emphasis">{{ $t('student.dashboard.noTests') }}</p>
              <v-btn
                v-if="!isLimitedStudent"
                color="primary"
                class="mt-3"
                @click="$router.push('/student/tests')"
              >
                {{ $t('test.startTest') }}
              </v-btn>
              <v-btn
                v-else
                color="warning"
                variant="outlined"
                class="mt-3"
                disabled
              >
                {{ $t('student.restrictions.testRestricted') }}
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Rounds Progress -->
          <v-card elevation="4" class="mb-6">
            <v-card-title class="d-flex align-center">
              <v-icon icon="mdi-chart-timeline-variant" class="me-2" />
              {{ $t('adminUsers.rounds.title') }}
              <v-chip v-if="currentRound" size="small" color="info" variant="tonal" class="ml-2">
                {{ $t('adminUsers.rounds.round') }} {{ currentRound }}
              </v-chip>
              <v-spacer />
              <v-chip v-if="rounds.length > 0" size="small" variant="text">
                {{ rounds.reduce((sum, r) => sum + r.answered, 0) }} {{ $t('adminUsers.rounds.totalAnswered') || 'жауап' }}
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text v-if="rounds.length > 0" class="pa-0">
              <v-table density="compact">
                <thead>
                  <tr>
                    <th>{{ $t('adminUsers.rounds.round') }}</th>
                    <th class="text-success">{{ $t('adminUsers.rounds.correct') }}</th>
                    <th class="text-error">{{ $t('adminUsers.rounds.incorrect') }}</th>
                        <th class="text-info">{{ $t('student.progress.correctAnswerRate') }}</th>
                    <th>{{ $t('adminUsers.rounds.progress') }}</th>
                    <th>{{ $t('adminUsers.rounds.date') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in rounds" :key="r.round" :style="r.round === currentRound ? 'background: rgba(33, 150, 243, 0.15)' : ''">
                    <td class="font-weight-bold">
                      {{ r.round }}
                      <v-chip v-if="r.round === currentRound" size="x-small" color="info" variant="flat" class="ml-1">
                        {{ $t('adminUsers.rounds.current') }}
                      </v-chip>
                    </td>
                    <td class="text-success font-weight-bold">{{ r.correct }}</td>
                    <td class="text-error font-weight-bold">{{ r.incorrect }}</td>
                    <td class="text-info font-weight-bold">{{ r.percentage }}%</td>
                    <td>
                      <div class="d-flex align-center gap-2">
                        <v-progress-linear :model-value="r.progress" :color="r.progress >= 100 ? 'success' : 'primary'" height="6" rounded style="min-width: 40px; max-width: 80px;" />
                        <span class="text-caption">{{ r.answered }}/{{ r.totalTests }} ({{ r.progress }}%)</span>
                      </div>
                    </td>
                    <td class="text-caption text-no-wrap">
                      <div v-if="r.firstDate">{{ formatShortDate(r.firstDate) }}</div>
                      <div v-if="r.lastDate && r.lastDate !== r.firstDate" class="text-medium-emphasis">{{ formatShortDate(r.lastDate) }}</div>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-card-text>
            <v-card-text v-else class="text-center pa-8">
              <v-icon icon="mdi-chart-timeline-variant" size="48" class="text-medium-emphasis mb-3" />
              <p class="text-medium-emphasis">{{ $t('student.dashboard.noRoundsData') || 'Раунд деректері жоқ. Тест тапсырыңыз!' }}</p>
              <v-btn
                v-if="!isLimitedStudent"
                color="primary"
                variant="outlined"
                class="mt-2"
                @click="$router.push('/student/tests')"
              >
                {{ $t('test.startTest') }}
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- ============================================ -->
          <!-- FULL PROGRESS SECTIONS -->
          <!-- ============================================ -->

          <!-- Exam Countdown + Daily Streak -->
          <v-row class="mb-6">
            <v-col cols="12" md="6">
              <v-card class="h-100" :color="examCountdown.isPast ? 'error' : 'primary'" variant="tonal">
                <v-card-text>
                  <div class="d-flex align-center mb-3">
                    <v-icon size="32" class="me-3">mdi-calendar-clock</v-icon>
                    <h3 class="text-h6">{{ $t('student.progress.examCountdown') }}</h3>
                  </div>
                  <div v-if="examCountdown.isSet" class="text-center py-4">
                    <div class="text-h2 font-weight-bold" :class="examCountdown.isPast ? 'text-error' : 'text-primary'">
                      {{ examCountdown.daysLeft }}
                    </div>
                    <div class="text-body-1 mt-1">{{ examCountdown.isPast ? $t('student.progress.daysPassed') : $t('student.progress.daysLeft') }}</div>
                    <div class="text-caption text-medium-emphasis mt-2">
                      {{ formatFullDate(examCountdown.examDate) }}
                    </div>
                    <v-btn variant="text" size="small" color="error" class="mt-2" @click="clearExamDate">
                      {{ $t('student.progress.remove') }}
                    </v-btn>
                  </div>
                  <div v-else class="text-center py-4">
                    <v-icon size="48" color="grey" class="mb-3">mdi-calendar-question</v-icon>
                    <p class="text-body-2 text-medium-emphasis mb-3">{{ $t('student.progress.setExamDate') }}</p>
                    <v-text-field
                      v-model="newExamDate"
                      type="date"
                      :label="$t('student.progress.examDate')"
                      variant="outlined"
                      density="compact"
                      class="mx-auto"
                      style="max-width: 250px"
                    />
                    <v-btn color="primary" variant="flat" size="small" :disabled="!newExamDate" @click="setExamDate" class="mt-2">
                      {{ $t('student.progress.save') }}
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card class="h-100" color="orange" variant="tonal">
                <v-card-text>
                  <div class="d-flex align-center mb-3">
                    <v-icon size="32" class="me-3">mdi-fire</v-icon>
                    <h3 class="text-h6">{{ $t('student.progress.dailyStreak') }}</h3>
                  </div>
                  <div class="text-center py-2">
                    <div class="d-flex justify-center align-center ga-6">
                      <div>
                        <div class="text-h2 font-weight-bold text-orange">{{ progressStreak.currentStreak }}</div>
                        <div class="text-body-2">{{ $t('student.progress.currentStreak') }}</div>
                      </div>
                      <v-divider vertical class="mx-2" />
                      <div>
                        <div class="text-h4 font-weight-bold">{{ progressStreak.longestStreak }}</div>
                        <div class="text-body-2">{{ $t('student.progress.longestStreak') }}</div>
                      </div>
                    </div>
                    <div class="d-flex justify-center ga-2 mt-4">
                      <div v-for="day in progressStreak.weekDays" :key="day.date" class="text-center">
                        <v-avatar :color="day.active ? 'orange' : 'grey-lighten-3'" size="36" class="mb-1">
                          <v-icon v-if="day.active" size="18" color="white">mdi-check</v-icon>
                        </v-avatar>
                        <div class="text-caption">{{ day.dayName }}</div>
                      </div>
                    </div>
                    <div class="text-caption text-medium-emphasis mt-2">
                      {{ $t('student.progress.totalActiveDays') }}: {{ progressStreak.totalActiveDays }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Overall Progress Card -->
          <v-card class="mb-6" color="primary" variant="tonal">
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="8">
                  <h3 class="text-h5 mb-3">{{ $t('student.progress.overall') }}</h3>
                  <div class="d-flex align-center mb-4">
                    <div class="me-4">
                      <div class="text-h3 text-primary">{{ overallProgress }}%</div>
                      <div class="text-body-2">{{ $t('student.progress.completed') }}</div>
                    </div>
                    <v-progress-linear :model-value="overallProgress" height="12" rounded color="primary" class="flex-grow-1" />
                  </div>
                  <div class="text-body-2 text-medium-emphasis">{{ $t('student.progress.overallDesc') }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-center">
                    <v-avatar size="120" color="white">
                      <v-icon size="60" :color="getProgressColor(overallProgress)">{{ getProgressIcon(overallProgress) }}</v-icon>
                    </v-avatar>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Progress Stats Cards -->
          <v-row class="mb-6">
            <v-col cols="12" sm="6" md="3">
              <v-card class="stat-card" color="success" variant="tonal">
                <v-card-text class="text-center">
                  <v-icon size="48" class="mb-3">mdi-book-check</v-icon>
                  <div class="text-h4">{{ progressStats.testsCompleted }}</div>
                  <div class="text-body-2">{{ $t('student.progress.testsCompleted') }}</div>
                  <div class="text-caption text-medium-emphasis mt-1">{{ progressStats.averageTestScore }}% {{ $t('student.progress.averageScore') }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="stat-card" color="info" variant="tonal">
                <v-card-text class="text-center">
                  <v-icon size="48" class="mb-3">mdi-car</v-icon>
                  <div class="text-h4">{{ progressStats.lessonsCompleted }}</div>
                  <div class="text-body-2">{{ $t('student.progress.lessonsCompleted') }}</div>
                  <div class="text-caption text-medium-emphasis mt-1">{{ progressStats.totalHours }} {{ $t('student.progress.hours') }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="stat-card" color="warning" variant="tonal">
                <v-card-text class="text-center">
                  <v-icon size="48" class="mb-3">mdi-clock-outline</v-icon>
                  <div class="text-h4">{{ progressStats.studyTime }}</div>
                  <div class="text-body-2">{{ $t('student.progress.studyTime') }}</div>
                  <div class="text-caption text-medium-emphasis mt-1">{{ $t('student.progress.thisMonth') }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="stat-card" color="purple" variant="tonal">
                <v-card-text class="text-center">
                  <v-icon size="48" class="mb-3">mdi-target</v-icon>
                  <div class="text-h4">{{ badges.unlockedCount }}/{{ badges.totalCount }}</div>
                  <div class="text-body-2">{{ $t('student.progress.achievements') }}</div>
                  <div class="text-caption text-medium-emphasis mt-1">{{ $t('student.progress.unlocked') }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Score Dynamics Chart -->
          <v-card class="mb-6">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2">mdi-chart-line</v-icon>
              {{ $t('student.progress.scoreDynamics') }}
              <v-spacer />
              <v-chip
                v-if="scoreHistory.trend"
                :color="scoreHistory.trend === 'up' ? 'success' : scoreHistory.trend === 'down' ? 'error' : 'grey'"
                size="small" variant="tonal"
              >
                <v-icon size="14" class="me-1">{{ scoreHistory.trend === 'up' ? 'mdi-trending-up' : scoreHistory.trend === 'down' ? 'mdi-trending-down' : 'mdi-minus' }}</v-icon>
                {{ scoreHistory.trend === 'up' ? $t('student.progress.trendUp') : scoreHistory.trend === 'down' ? $t('student.progress.trendDown') : $t('student.progress.trendStable') }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <div v-if="scoreHistory.data.length > 1" style="height: 300px; position: relative;">
                <Line :data="lineChartData" :options="lineChartOptions" />
              </div>
              <div v-else class="text-center py-8">
                <v-icon size="60" color="grey-lighten-2" class="mb-3">mdi-chart-line-variant</v-icon>
                <p class="text-body-2 text-medium-emphasis">{{ $t('student.progress.chartEmpty') }}</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Weak Topics + Recommended Tests -->
          <v-row class="mb-6">
            <v-col cols="12" md="6">
              <v-card class="h-100">
                <v-card-title class="d-flex align-center">
                  <v-icon class="me-2" color="error">mdi-alert-circle</v-icon>
                  {{ $t('student.progress.weakTopics') }}
                </v-card-title>
                <v-card-text>
                  <div v-if="weakTopics.length > 0">
                    <div v-for="topic in weakTopics" :key="topic.category" class="mb-4">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-body-2">{{ formatCategoryName(topic.category) }}</span>
                        <v-chip size="x-small" :color="topic.errorRate > 50 ? 'error' : topic.errorRate > 30 ? 'warning' : 'success'" variant="tonal">
                          {{ topic.errorRate }}% {{ $t('student.progress.errorRate') }}
                        </v-chip>
                      </div>
                      <v-progress-linear :model-value="topic.successRate" height="8" rounded :color="topic.successRate >= 70 ? 'success' : topic.successRate >= 50 ? 'warning' : 'error'" />
                      <div class="d-flex justify-space-between text-caption text-medium-emphasis mt-1">
                        <span>{{ topic.correct }} {{ $t('student.progress.correct') }} / {{ topic.incorrect }} {{ $t('student.progress.incorrect') }}</span>
                        <span>{{ topic.total }} {{ $t('student.progress.answers') }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-6">
                    <v-icon size="48" color="grey-lighten-2" class="mb-2">mdi-shield-check</v-icon>
                    <p class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noWeakTopics') }}</p>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card class="h-100">
                <v-card-title class="d-flex align-center">
                  <v-icon class="me-2" color="info">mdi-lightbulb-on</v-icon>
                  {{ $t('student.progress.recommendedTests') }}
                </v-card-title>
                <v-card-text>
                  <div v-if="weakCategories.length > 0">
                    <v-list density="compact">
                      <v-list-item v-for="(cat, idx) in weakCategories" :key="idx" class="px-0" @click="startCategoryTest(cat.category)">
                        <template #prepend>
                          <v-icon size="small" :color="cat.errorRate > 50 ? 'error' : cat.errorRate > 30 ? 'warning' : 'info'">
                            {{ cat.errorRate > 50 ? 'mdi-alert-circle' : cat.errorRate > 30 ? 'mdi-alert' : 'mdi-information' }}
                          </v-icon>
                        </template>
                        <v-list-item-title class="text-body-2">{{ formatCategoryName(cat.category) }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          {{ cat.errorCount }} {{ $t('student.progress.errors') }} · {{ cat.errorRate }}% {{ $t('student.progress.errorRate') }}
                        </v-list-item-subtitle>
                        <template #append>
                          <v-icon size="small" color="primary">mdi-play-circle-outline</v-icon>
                        </template>
                      </v-list-item>
                    </v-list>
                    <v-btn color="primary" variant="outlined" size="small" block class="mt-3" @click="startRecommendedTest">
                      <v-icon start>mdi-play</v-icon>
                      {{ $t('student.progress.startRecommended') }}
                    </v-btn>
                  </div>
                  <div v-else class="text-center py-6">
                    <v-icon size="48" color="grey-lighten-2" class="mb-2">mdi-check-all</v-icon>
                    <p class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noRecommendations') }}</p>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Course Progress -->
          <v-card class="mb-6">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2">mdi-play-circle</v-icon>
              {{ $t('student.progress.courseProgress') }}
              <v-spacer />
              <v-chip color="primary" variant="tonal" size="small">{{ courseProgress.overallProgress }}% {{ $t('student.progress.completedPercent') }}</v-chip>
            </v-card-title>
            <v-card-text>
              <div class="mb-4">
                <v-progress-linear :model-value="courseProgress.overallProgress" height="10" rounded color="primary" />
                <div class="d-flex justify-space-between text-caption text-medium-emphasis mt-1">
                  <span>{{ courseProgress.completedLessons }} / {{ courseProgress.totalLessons }} {{ $t('student.progress.lessonsCount') }}</span>
                  <span>{{ courseProgress.totalWatchedMinutes }} {{ $t('student.progress.minutesWatched') }}</span>
                </div>
              </div>
              <v-expansion-panels v-if="courseProgress.categories.length > 0" variant="accordion">
                <v-expansion-panel v-for="category in courseProgress.categories" :key="category.id">
                  <v-expansion-panel-title>
                    <div class="d-flex align-center w-100 me-4">
                      <span class="flex-grow-1">{{ category.title }}</span>
                      <v-chip size="x-small" :color="getProgressColor(category.progress)" variant="tonal" class="ms-2">{{ category.completedLessons }}/{{ category.totalLessons }}</v-chip>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-list density="compact">
                      <v-list-item v-for="lesson in category.lessons" :key="lesson.id" class="px-0">
                        <template #prepend>
                          <v-icon :color="lesson.completed ? 'success' : 'grey'" size="small">{{ lesson.completed ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                        </template>
                        <v-list-item-title class="text-body-2">{{ lesson.title }}</v-list-item-title>
                        <template #append>
                          <v-progress-linear :model-value="lesson.watchedPercent" height="4" rounded :color="lesson.completed ? 'success' : 'primary'" style="width: 60px" />
                          <span class="text-caption ms-2" style="min-width: 35px">{{ lesson.watchedPercent }}%</span>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
              <div v-else class="text-center py-4">
                <v-icon size="48" color="grey-lighten-2">mdi-video-off</v-icon>
                <p class="text-body-2 text-medium-emphasis mt-2">{{ $t('student.progress.noVideoLessons') }}</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Theory + Practice Progress -->
          <v-row class="mb-6">
            <v-col cols="12" md="6">
              <v-card class="h-100">
                <v-card-title class="d-flex align-center">
                  <v-icon class="me-2">mdi-book-open-variant</v-icon>
                  {{ $t('student.progress.theoryProgress') }}
                </v-card-title>
                <v-card-text>
                  <div class="mb-4">
                    <div class="d-flex justify-space-between mb-2">
                      <span class="text-body-1">{{ $t('student.progress.overallTheory') }}</span>
                      <span class="text-body-1 font-weight-bold">{{ theoryProgress }}%</span>
                    </div>
                    <v-progress-linear :model-value="theoryProgress" height="8" rounded :color="getProgressColor(theoryProgress)" />
                  </div>
                  <div class="mb-4">
                    <h4 class="text-subtitle-1 mb-3">{{ $t('student.progress.categories') }}</h4>
                    <div v-for="cat in theoryCategories" :key="cat.name" class="mb-3">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-body-2">{{ cat.name }}</span>
                        <div class="d-flex align-center">
                          <span class="text-caption me-2">{{ cat.completed }}/{{ cat.total }}</span>
                          <v-chip size="x-small" :color="getProgressColor(cat.progress)" variant="tonal">{{ cat.progress }}%</v-chip>
                        </div>
                      </div>
                      <v-progress-linear :model-value="cat.progress" height="4" rounded :color="getProgressColor(cat.progress)" />
                    </div>
                  </div>
                  <div>
                    <h4 class="text-subtitle-1 mb-3">{{ $t('student.progress.recentTests') }}</h4>
                    <div v-if="progressRecentTests.length > 0">
                      <v-list density="compact">
                        <v-list-item v-for="pTest in progressRecentTests" :key="pTest.id" class="px-0">
                          <template #prepend>
                            <v-icon size="small" :color="pTest.score >= 80 ? 'success' : 'error'">{{ pTest.score >= 80 ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                          </template>
                          <v-list-item-title class="text-body-2">{{ formatProgressDate(pTest.date) }}</v-list-item-title>
                          <template #append>
                            <v-chip size="small" :color="pTest.score >= 80 ? 'success' : 'error'" variant="tonal">{{ pTest.score }}%</v-chip>
                          </template>
                        </v-list-item>
                      </v-list>
                    </div>
                    <div v-else class="text-center py-4">
                      <v-icon size="40" color="grey-lighten-2" class="mb-2">mdi-clipboard-text-outline</v-icon>
                      <div class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noTests') }}</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card class="h-100">
                <v-card-title class="d-flex align-center">
                  <v-icon class="me-2">mdi-steering</v-icon>
                  {{ $t('student.progress.practiceProgress') }}
                </v-card-title>
                <v-card-text>
                  <div class="mb-4">
                    <div class="d-flex justify-space-between mb-2">
                      <span class="text-body-1">{{ $t('student.progress.overallPractice') }}</span>
                      <span class="text-body-1 font-weight-bold">{{ practiceProgress }}%</span>
                    </div>
                    <v-progress-linear :model-value="practiceProgress" height="8" rounded :color="getProgressColor(practiceProgress)" />
                  </div>
                  <div class="mb-4">
                    <h4 class="text-subtitle-1 mb-3">{{ $t('student.progress.skills') }}</h4>
                    <div v-for="skill in practiceSkills" :key="skill.name" class="mb-3">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-body-2">{{ skill.name }}</span>
                        <div class="d-flex align-center">
                          <v-rating :model-value="skill.rating" readonly color="amber" size="small" density="compact" class="me-2" />
                          <v-chip size="x-small" :color="getSkillColor(skill.rating)" variant="tonal">{{ skill.rating }}/5</v-chip>
                        </div>
                      </div>
                      <v-progress-linear :model-value="(skill.rating / 5) * 100" height="4" rounded :color="getSkillColor(skill.rating)" />
                    </div>
                  </div>
                  <div>
                    <h4 class="text-subtitle-1 mb-3">{{ $t('student.progress.recentLessons') }}</h4>
                    <div v-if="progressRecentLessons.length > 0">
                      <v-list density="compact">
                        <v-list-item v-for="pLesson in progressRecentLessons" :key="pLesson.id" class="px-0">
                          <template #prepend>
                            <v-icon size="small" :color="getLessonStatusColor(pLesson.status)">{{ getLessonStatusIcon(pLesson.status) }}</v-icon>
                          </template>
                          <v-list-item-title class="text-body-2">{{ formatProgressDate(pLesson.date) }} - {{ pLesson.timeSlot }}</v-list-item-title>
                          <template #append>
                            <v-chip size="small" :color="getLessonStatusColor(pLesson.status)" variant="tonal">{{ $t(`schedule.status.${pLesson.status}`) || pLesson.status }}</v-chip>
                          </template>
                        </v-list-item>
                      </v-list>
                    </div>
                    <div v-else class="text-center py-4">
                      <v-icon size="40" color="grey-lighten-2" class="mb-2">mdi-car-outline</v-icon>
                      <div class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noLessons') }}</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Badges -->
          <v-card class="mb-6">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2">mdi-trophy</v-icon>
              {{ $t('student.progress.badges') }}
              <v-spacer />
              <v-chip color="primary" variant="tonal" size="small">{{ badges.unlockedCount }} / {{ badges.totalCount }}</v-chip>
            </v-card-title>
            <v-card-text>
              <v-row v-if="badges.list.length > 0">
                <v-col v-for="badge in badges.list" :key="badge.key" cols="6" sm="4" md="3" lg="2">
                  <v-card class="achievement-card text-center pa-3" :class="{ 'unlocked': badge.unlocked }" :color="badge.unlocked ? badge.color : 'grey-lighten-3'" variant="tonal">
                    <v-icon size="40" :color="badge.unlocked ? badge.color : 'grey'" class="mb-2">{{ badge.icon }}</v-icon>
                    <h4 class="text-subtitle-2 mb-1">{{ badge.title }}</h4>
                    <p class="text-caption text-medium-emphasis" style="min-height: 32px">{{ badge.description }}</p>
                    <div v-if="badge.unlocked" class="mt-2">
                      <v-chip size="x-small" color="success" variant="tonal">{{ $t('student.progress.unlocked') }} ✓</v-chip>
                    </div>
                    <div v-else class="mt-2">
                      <v-progress-linear :model-value="badge.progress" height="4" rounded color="grey" />
                      <div class="text-caption text-medium-emphasis mt-1">{{ badge.progress }}%</div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
              <div v-else class="text-center py-8">
                <v-icon size="80" color="grey-lighten-2" class="mb-4">mdi-trophy-outline</v-icon>
                <h3 class="text-h6 mb-2">{{ $t('student.progress.noBadges') }}</h3>
                <p class="text-body-2 text-medium-emphasis">{{ $t('student.progress.noBadgesDesc') }}</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Study Plan -->
          <v-card class="mb-6">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2">mdi-calendar-check</v-icon>
              {{ $t('student.progress.studyPlan') }}
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <h4 class="text-subtitle-1 mb-3">{{ $t('student.progress.recommendations') }}</h4>
                  <v-list>
                    <v-list-item v-for="rec in progressRecommendations" :key="rec.id" :prepend-icon="rec.icon" :title="rec.title" :subtitle="rec.description">
                      <template #append>
                        <v-btn :color="rec.color" variant="outlined" size="small" @click="handleRecommendation(rec)">{{ rec.action }}</v-btn>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12" md="6">
                  <h4 class="text-subtitle-1 mb-3">{{ $t('student.progress.goals') }}</h4>
                  <div v-for="goal in progressGoals" :key="goal.id" class="mb-4">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <span class="text-body-1">{{ goal.title }}</span>
                      <v-chip size="small" :color="goal.color" variant="tonal">{{ goal.progress }}%</v-chip>
                    </div>
                    <v-progress-linear :model-value="goal.progress" height="6" rounded :color="goal.color" />
                    <div class="text-caption text-medium-emphasis mt-1">{{ goal.description }}</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Sidebar -->
        <v-col cols="12" lg="4">
          <v-card class="mb-6" elevation="4">
            <v-card-title>
              <v-icon icon="mdi-telegram" color="info" class="me-2" />
              Telegram
            </v-card-title>
            <v-divider />
            <v-card-text>
              <p class="text-body-2 text-medium-emphasis mb-4">
                {{ $t('student.dashboard.telegramDescription') }}
              </p>
              <v-btn
                color="info"
                variant="elevated"
                block
                :loading="telegramLinkLoading"
                @click="openTelegramLink"
              >
                <v-icon start>mdi-telegram</v-icon>
                {{ $t('student.dashboard.telegramSubscribe') }}
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Next Lesson -->
          <v-card class="mb-6" elevation="4">
            <v-card-title>
              <v-icon icon="mdi-calendar-clock" class="me-2" />
              {{ $t('student.dashboard.nextLesson') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div v-if="nextLesson" class="next-lesson-card">
                <div class="text-center mb-3">
                  <v-icon icon="mdi-calendar" size="48" color="primary" />
                </div>
                <div class="mb-3">
                  <div class="text-subtitle-2 text-medium-emphasis">{{ $t('common.date') }}</div>
                  <div class="text-h6">{{ formatDate(nextLesson.date) }}</div>
                </div>
                <div class="mb-3">
                  <div class="text-subtitle-2 text-medium-emphasis">{{ $t('common.time') }}</div>
                  <div class="text-h6">{{ nextLesson.timeSlot }}</div>
                </div>
                <div class="mb-3">
                  <div class="text-subtitle-2 text-medium-emphasis">{{ $t('common.instructor') }}</div>
                  <div class="text-h6">{{ nextLesson.instructor?.fio }}</div>
                </div>
              </div>
              <div v-else class="text-center py-4">
                <v-icon icon="mdi-calendar-remove" size="48" class="text-medium-emphasis mb-2" />
                <p class="text-medium-emphasis">{{ $t('student.dashboard.noUpcomingLessons') }}</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Quick Actions -->
          <v-card class="mb-6" elevation="4">
            <v-card-title>
              <v-icon icon="mdi-lightning-bolt" class="me-2" />
              {{ $t('student.dashboard.quickActions') }}
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-2">
              <v-list>
                <v-list-item 
                  v-if="!isLimitedStudent"
                  @click="$router.push('/student/tests')" 
                  prepend-icon="mdi-test-tube"
                >
                  <v-list-item-title>{{ $t('nav.tests') }}</v-list-item-title>
                </v-list-item>
                <v-list-item 
                  v-else
                  prepend-icon="mdi-test-tube-off" 
                  disabled
                >
                  <v-list-item-title class="text-medium-emphasis">
                    {{ $t('nav.tests') }} ({{ $t('student.restrictions.restricted') }})
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/student/schedule')" prepend-icon="mdi-calendar">
                  <v-list-item-title>{{ $t('nav.schedule') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/student/instructors')" prepend-icon="mdi-account-group">
                  <v-list-item-title>{{ $t('nav.instructors') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/student/profile')" prepend-icon="mdi-account">
                  <v-list-item-title>{{ $t('nav.profile') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openTelegramLink" prepend-icon="mdi-telegram">
                  <v-list-item-title>{{ $t('student.dashboard.telegramSubscribe') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>

          <!-- Tips Card -->
          <v-card elevation="4">
            <v-card-title>
              <v-icon icon="mdi-lightbulb" class="me-2" />
              {{ $t('student.dashboard.tips') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-list density="compact">
                <v-list-item
                  v-for="(tip, index) in tips"
                  :key="index"
                  class="px-0"
                >
                  <template #prepend>
                    <v-icon icon="mdi-check-circle" color="success" size="small" class="me-3" />
                  </template>
                  <v-list-item-title class="text-body-2">{{ tip }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Telegram Connect (bottom) -->
      <v-row class="mt-4 mb-2">
        <v-col cols="12">
          <v-alert
            color="info"
            variant="tonal"
            border="start"
            class="telegram-connect-alert"
          >
            <template #prepend>
              <v-icon icon="mdi-telegram" />
            </template>
            <div class="telegram-connect-content">
              <div>
                <div class="text-subtitle-1 font-weight-bold">{{ $t('student.dashboard.telegramTitle') }}</div>
                <div class="text-body-2 text-medium-emphasis">
                  {{ $t('student.dashboard.telegramDescription') }}
                </div>
              </div>
              <v-btn
                color="info"
                variant="elevated"
                :block="$vuetify.display.xs"
                :loading="telegramLinkLoading"
                @click="openTelegramLink"
              >
                <v-icon start>mdi-telegram</v-icon>
                {{ $t('student.dashboard.telegramSubscribe') }}
              </v-btn>
            </div>
            <div v-if="telegramManualCommand" class="telegram-code-fallback">
              <div class="text-caption text-medium-emphasis">
                {{ $t('student.dashboard.telegramFallback') }}
              </div>
              <div class="telegram-code-row">
                <code>{{ telegramManualCommand }}</code>
                <v-btn
                  icon="mdi-content-copy"
                  size="x-small"
                  variant="text"
                  color="info"
                  :aria-label="$t('student.dashboard.telegramCopyCode')"
                  @click="copyTelegramCode"
                />
              </div>
            </div>
          </v-alert>
        </v-col>
      </v-row>
    </v-container>

    <!-- Test Details Dialog -->
    <v-dialog v-model="showTestDetailsDialog" max-width="600">
      <v-card v-if="selectedTest">
        <v-card-title>
          {{ $t('test.details') }}
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <div class="text-subtitle-2 text-medium-emphasis">{{ $t('test.mode') }}</div>
              <div class="text-h6">{{ getModeText(selectedTest.mode) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-subtitle-2 text-medium-emphasis">{{ $t('test.score') }}</div>
              <div class="text-h6">{{ selectedTest.score }}%</div>
            </v-col>
            <v-col cols="6">
              <div class="text-subtitle-2 text-medium-emphasis">{{ $t('test.correctAnswers') }}</div>
              <div class="text-h6">{{ getCorrectAnswersCount(selectedTest) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-subtitle-2 text-medium-emphasis">{{ $t('test.timeSpent') }}</div>
              <div class="text-h6">{{ formatTimeSpent(selectedTest.timeSpent) }}</div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="showTestDetailsDialog = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSnackbarStore } from '@/stores/snackbar'
import api from '@/utils/api'
import { Line } from 'vue-chartjs'
import StudentScheduleView from '@/views/student/Schedule.vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const snackbar = useSnackbarStore()

// ==========================================
// Dashboard State
// ==========================================
const loading = ref(false)
const telegramLinkLoading = ref(false)
const telegramLinkCode = ref('')
const telegramManualCommand = computed(() => telegramLinkCode.value ? `/link ${telegramLinkCode.value}` : '')
const stats = ref({
  totalTests: 0,
  averageScore: 0,
  lessonsThisWeek: 0,
  studyHours: 0
})
const recentTests = ref([])
const nextLesson = ref(null)
const rounds = ref([])
const currentRound = ref(1)
const showTestDetailsDialog = ref(false)
const selectedTest = ref(null)

// LIMITED_STUDENT logic
const isLimitedStudent = computed(() => authStore.user?.role === 'LIMITED_STUDENT')
const showRestrictionAlert = ref(true)

const tips = computed(() => [
  t('student.dashboard.tip1'),
  t('student.dashboard.tip2'),
  t('student.dashboard.tip3'),
  t('student.dashboard.tip4')
])

const openTelegramLink = async () => {
  telegramLinkLoading.value = true
  try {
    const response = await api.post('/student/telegram-link')
    telegramLinkCode.value = response.data?.code || ''
    if (response.data?.link) {
      window.open(response.data.link, '_blank', 'noopener,noreferrer')
      snackbar.success(t('student.dashboard.telegramOpened'))
    } else {
      throw new Error(t('student.dashboard.telegramLinkNotReceived'))
    }
  } catch (error) {
    snackbar.error(error.response?.data?.error || error.message || t('student.dashboard.telegramLinkError'))
  } finally {
    telegramLinkLoading.value = false
  }
}

const copyTelegramCode = async () => {
  if (!telegramManualCommand.value) return
  try {
    await navigator.clipboard.writeText(telegramManualCommand.value)
    snackbar.success(t('student.dashboard.telegramCodeCopied'))
  } catch (_) {
    snackbar.info(telegramManualCommand.value)
  }
}

// ==========================================
// Progress State
// ==========================================
const progressStats = reactive({
  testsCompleted: 0,
  averageTestScore: 0,
  lessonsCompleted: 0,
  totalHours: 0,
  studyTime: '0ч',
  achievementsCount: 0
})

const theoryCategories = ref([])
const practiceSkills = ref([])
const progressRecentTests = ref([])
const progressRecentLessons = ref([])
const progressRecommendations = ref([])
const progressGoals = ref([])

const weakTopics = ref([])
const scoreHistory = reactive({ data: [], trend: 'neutral', totalTests: 0, overallAverage: 0 })
const progressStreak = reactive({
  currentStreak: 0,
  longestStreak: 0,
  totalActiveDays: 0,
  isActiveToday: false,
  weekDays: []
})
const badges = reactive({ list: [], unlockedCount: 0, totalCount: 0 })
const courseProgress = reactive({
  categories: [],
  totalLessons: 0,
  completedLessons: 0,
  overallProgress: 0,
  totalWatchedMinutes: 0
})
const examCountdown = reactive({
  examDate: null,
  daysLeft: null,
  isSet: false,
  isPast: false
})
const recommendedTests = ref([])
const weakCategories = ref([])
const ranking = reactive({
  myRank: null,
  totalStudents: 0,
  percentile: 0,
  myName: '',
  myCompletedRounds: 0,
  myAccuracy: 0,
  myProgress: 0,
  myAnswered: 0,
  myRankScore: 0,
  totalTestsInSystem: 0,
  topStudents: []
})
const incorrectQuestions = reactive({
  count: 0,
  testIds: []
})
const newExamDate = ref('')

// TOP 5 ranking + my position
const rankingTop5 = computed(() => {
  return ranking.topStudents.filter(s => s.rank <= 5)
})

const myRankOutsideTop5 = computed(() => {
  if (!ranking.myRank || ranking.myRank <= 5) return false
  return !ranking.topStudents.some(s => s.isMe && s.rank <= 5)
})

const myRankingRow = computed(() => {
  if (!myRankOutsideTop5.value) return null

  return {
    rank: ranking.myRank,
    name: ranking.myName || t('student.progress.you'),
    accuracy: ranking.myAccuracy,
    progress: ranking.myProgress,
    answered: ranking.myAnswered,
    totalTests: ranking.totalTestsInSystem,
    completedRounds: ranking.myCompletedRounds,
    rankScore: ranking.myRankScore,
    isMe: true
  }
})

const formatRankScore = (value) => Number(value || 0).toFixed(2)
const normalizeProgressValue = (value) => {
  const normalized = Number(value || 0)
  if (!Number.isFinite(normalized)) return 0
  return Math.max(0, Math.min(Math.round(normalized), 100))
}

const getRankingProgressState = (value) => {
  const progress = normalizeProgressValue(value)
  if (progress >= 85) return 'high'
  if (progress >= 60) return 'mid'
  return 'low'
}

// ==========================================
// Progress Chart Data (Line Chart)
// ==========================================
const lineChartData = computed(() => ({
  labels: scoreHistory.data.map(d => {
    const date = new Date(d.date)
    return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}`
  }),
  datasets: [
    {
      label: t('student.progress.avgScore'),
      data: scoreHistory.data.map(d => d.averageScore),
      borderColor: '#1976D2',
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6
    },
    {
      label: t('student.progress.maxScore'),
      data: scoreHistory.data.map(d => d.maxScore),
      borderColor: '#4CAF50',
      backgroundColor: 'transparent',
      borderDash: [5, 5],
      tension: 0.4,
      pointRadius: 2
    }
  ]
}))

const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: { color: '#aaa', font: { size: 12 } }
    },
    tooltip: {
      callbacks: {
        afterLabel: (ctx) => {
          const idx = ctx.dataIndex
          const item = scoreHistory.data[idx]
          return item ? `${t('student.progress.testsCount')}: ${item.testsCount}` : ''
        }
      }
    }
  },
  scales: {
    y: {
      min: 0,
      max: 100,
      ticks: { color: '#aaa', stepSize: 20 },
      grid: { color: 'rgba(255,255,255,0.05)' }
    },
    x: {
      ticks: { color: '#aaa', maxRotation: 45 },
      grid: { color: 'rgba(255,255,255,0.05)' }
    }
  }
}

// ==========================================
// Progress Computed
// ==========================================
const overallProgress = computed(() => theoryProgress.value)

const theoryProgress = computed(() => {
  if (theoryCategories.value.length === 0) return 0
  const total = theoryCategories.value.reduce((sum, cat) => sum + cat.progress, 0)
  return Math.round(total / theoryCategories.value.length)
})

const practiceProgress = computed(() => {
  if (practiceSkills.value.length === 0) return 0
  const total = practiceSkills.value.reduce((sum, skill) => sum + (skill.rating / 5 * 100), 0)
  return Math.round(total / practiceSkills.value.length)
})

// ==========================================
// Dashboard Methods
// ==========================================
const loadDashboardData = async () => {
  loading.value = true
  try {
    // Load statistics
    const statsResponse = await api.get('/student/progress/statistics')
    stats.value = {
      totalTests: statsResponse.data.testsCompleted || 0,
      averageScore: statsResponse.data.averageTestScore || 0,
      lessonsThisWeek: statsResponse.data.lessonsThisWeek || 0,
      studyHours: Math.round((statsResponse.data.studyTimeMinutes || 0) / 60)
    }

    // Load rounds data
    rounds.value = statsResponse.data.rounds || []
    currentRound.value = statsResponse.data.currentRound || 1

    // Load recent tests
    const testsResponse = await api.get('/tests/results', {
      params: { limit: 5 }
    })
    recentTests.value = testsResponse.data.results || []

    // TODO: Load next lesson from schedule API
    // nextLesson.value = ...
  } catch (error) {
    // Error handled silently
  } finally {
    loading.value = false
  }
}

const getModeColor = (mode) => {
  const colors = {
    'EXAM': 'warning',
    'QUESTIONS_40': 'primary',
    'TRAINING_MODE': 'success'
  }
  return colors[mode] || 'default'
}

const getModeText = (mode) => {
  const modes = {
    'EXAM': t('test.modes.exam'),
    'QUESTIONS_40': t('test.modes.questions40'),
    'TRAINING_MODE': t('test.modes.training')
  }
  return modes[mode] || mode
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatShortDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yy = String(date.getFullYear()).slice(-2)
  return `${dd}.${mm}.${yy}`
}

const formatTimeSpent = (seconds) => {
  if (!seconds) return `0 ${t('student.progress.timeMin')}`
  const minutes = Math.floor(seconds / 60)
  return `${minutes} ${t('student.progress.timeMin')}`
}

const getCorrectAnswersCount = (test) => {
  if (!test.totalQuestions) return 0
  return Math.round((test.score / 100) * test.totalQuestions)
}

const viewTestDetails = (test) => {
  selectedTest.value = test
  showTestDetailsDialog.value = true
}

// LIMITED_STUDENT methods
const dismissRestrictionAlert = () => {
  showRestrictionAlert.value = false
}

const contactAdmin = () => {
  router.push('/contact')
}

const checkRestrictionRedirect = () => {
  if (route.query.restricted === 'true') {
    const restrictedFeature = localStorage.getItem('korkem-auto-restrictedFeature')
    if (restrictedFeature) {
      snackbar.warning(t('student.restrictions.accessDenied', { feature: restrictedFeature }))
      localStorage.removeItem('korkem-auto-restrictedRoute')
      localStorage.removeItem('korkem-auto-restrictedFeature')
      router.replace({ query: {} })
    }
  }
}

// ==========================================
// Progress Methods
// ==========================================
const loadProgressData = async () => {
  try {
    await Promise.all([
      loadRanking(),
      loadIncorrectQuestionsSummary()
    ])
  } catch (error) {
    // Progress load errors handled per-call
  }
}

const loadStatistics = async () => {
  try {
    const response = await api.get('/student/progress/statistics')
    const data = response.data
    
    progressStats.testsCompleted = data.testsCompleted || 0
    progressStats.averageTestScore = data.averageTestScore || 0
    progressStats.lessonsCompleted = data.lessonsCompleted || 0
    progressStats.totalHours = data.totalHours || 0
    progressStats.studyTime = formatStudyTime(data.studyTimeMinutes || 0)
    progressStats.achievementsCount = data.achievementsCount || 0
    
    progressRecentTests.value = data.recentTests || []
    progressRecentLessons.value = data.recentLessons || []
  } catch (error) {
    loadMockStatistics()
  }
}

const loadTheoryProgress = async () => {
  try {
    const response = await api.get('/student/progress/theory')
    theoryCategories.value = response.data.categories || []
  } catch (error) {
    theoryCategories.value = [
      { name: t('student.progress.mockTheory1'), completed: 25, total: 30, progress: 83 },
      { name: t('student.progress.mockTheory2'), completed: 18, total: 25, progress: 72 },
      { name: t('student.progress.mockTheory3'), completed: 12, total: 20, progress: 60 },
      { name: t('student.progress.mockTheory4'), completed: 8, total: 15, progress: 53 },
      { name: t('student.progress.mockTheory5'), completed: 10, total: 12, progress: 83 }
    ]
  }
}

const loadPracticeProgress = async () => {
  try {
    const response = await api.get('/student/progress/practice')
    practiceSkills.value = response.data.skills || []
  } catch (error) {
    practiceSkills.value = [
      { name: t('student.progress.mockPractice1'), rating: 4.2 },
      { name: t('student.progress.mockPractice2'), rating: 3.8 },
      { name: t('student.progress.mockPractice3'), rating: 4.0 },
      { name: t('student.progress.mockPractice4'), rating: 3.5 },
      { name: t('student.progress.mockPractice5'), rating: 3.2 },
      { name: t('student.progress.mockPractice6'), rating: 2.8 }
    ]
  }
}

const loadWeakTopics = async () => {
  try {
    const response = await api.get('/student/progress/weak-topics')
    weakTopics.value = response.data.weakTopics || []
  } catch (error) {
    weakTopics.value = []
  }
}

const loadScoreHistory = async () => {
  try {
    const response = await api.get('/student/progress/score-history')
    scoreHistory.data = response.data.scoreHistory || []
    scoreHistory.trend = response.data.trend || 'neutral'
    scoreHistory.totalTests = response.data.totalTests || 0
    scoreHistory.overallAverage = response.data.overallAverage || 0
  } catch (error) {
    scoreHistory.data = []
  }
}

const loadStreak = async () => {
  try {
    const response = await api.get('/student/progress/streak')
    progressStreak.currentStreak = response.data.currentStreak || 0
    progressStreak.longestStreak = response.data.longestStreak || 0
    progressStreak.totalActiveDays = response.data.totalActiveDays || 0
    progressStreak.isActiveToday = response.data.isActiveToday || false
    progressStreak.weekDays = response.data.weekDays || []
  } catch (error) {
    progressStreak.currentStreak = 0
    progressStreak.longestStreak = 0
    progressStreak.weekDays = []
  }
}

const loadBadges = async () => {
  try {
    const response = await api.get('/student/progress/badges')
    // Populate badges state (not ranking) with safe defaults
    badges.list = response.data.badges ?? []
    badges.unlockedCount = response.data.unlockedCount ?? 0
    badges.totalCount = response.data.totalCount ?? 0
  } catch (error) {
    // Reset badges on error to avoid stale data
    badges.list = []
    badges.unlockedCount = 0
    badges.totalCount = 0
    console.error('Failed to load badges:', error)
  }
}

const loadCourseProgress = async () => {
  try {
    const response = await api.get('/student/progress/course-progress')
    courseProgress.categories = response.data.courseProgress || []
    courseProgress.totalLessons = response.data.totalLessons || 0
    courseProgress.completedLessons = response.data.completedLessons || 0
    courseProgress.overallProgress = response.data.overallProgress || 0
    courseProgress.totalWatchedMinutes = response.data.totalWatchedMinutes || 0
  } catch (error) {
    courseProgress.categories = []
  }
}

const loadExamCountdown = async () => {
  try {
    const response = await api.get('/student/progress/exam-countdown')
    examCountdown.examDate = response.data.examDate || null
    examCountdown.daysLeft = response.data.daysLeft
    examCountdown.isSet = response.data.isSet || false
    examCountdown.isPast = response.data.isPast || false
  } catch (error) {
    examCountdown.isSet = false
  }
}

const loadRecommendedTests = async () => {
  try {
    const response = await api.get('/student/progress/recommended-tests')
    recommendedTests.value = response.data.recommendedTests || []
    
    // Build weak categories list from weakCategories + weakTopics data
    const cats = response.data.weakCategories || []
    if (cats.length > 0 && weakTopics.value.length > 0) {
      // Match with weakTopics to get error stats
      weakCategories.value = cats.map(cat => {
        const topic = weakTopics.value.find(t => t.category === cat)
        return {
          category: cat,
          errorCount: topic ? topic.incorrect : 0,
          errorRate: topic ? topic.errorRate : 0
        }
      }).sort((a, b) => b.errorRate - a.errorRate)
    } else if (cats.length > 0) {
      weakCategories.value = cats.map(cat => ({
        category: cat,
        errorCount: 0,
        errorRate: 0
      }))
    } else {
      weakCategories.value = []
    }
  } catch (error) {
    recommendedTests.value = []
    weakCategories.value = []
  }
}

const loadRanking = async () => {
  try {
    const response = await api.get('/student/progress/ranking')
    ranking.myRank = response.data.myRank || null
    ranking.totalStudents = response.data.totalStudents || 0
    ranking.percentile = response.data.percentile || 0
    ranking.myName = response.data.myName || ''
    ranking.myCompletedRounds = response.data.myCompletedRounds || 0
    ranking.myAccuracy = response.data.myAccuracy || 0
    ranking.myProgress = response.data.myProgress || 0
    ranking.myAnswered = response.data.myAnswered || 0
    ranking.myRankScore = response.data.myRankScore || 0
    ranking.totalTestsInSystem = response.data.totalTestsInSystem || 0
    ranking.topStudents = response.data.topStudents || []
  } catch (error) {
    console.error('Error loading ranking:', error)
    ranking.myRank = null
    ranking.totalStudents = 0
    ranking.percentile = 0
    ranking.myName = ''
    ranking.myCompletedRounds = 0
    ranking.myAccuracy = 0
    ranking.myProgress = 0
    ranking.myAnswered = 0
    ranking.myRankScore = 0
    ranking.totalTestsInSystem = 0
    ranking.topStudents = []
  }
}

const loadIncorrectQuestionsSummary = async () => {
  try {
    const response = await api.get('/tests/incorrect-questions')
    incorrectQuestions.count = response.data.count || 0
    incorrectQuestions.testIds = response.data.testIds || []
  } catch (error) {
    incorrectQuestions.count = 0
    incorrectQuestions.testIds = []
  }
}

const startIncorrectQuestionsReview = () => {
  if (!incorrectQuestions.count) return
  router.push('/student/tests?mistakes=1')
}

const setExamDate = async () => {
  if (!newExamDate.value) return
  try {
    const response = await api.put('/student/progress/exam-countdown', {
      examDate: newExamDate.value
    })
    examCountdown.examDate = response.data.examDate
    examCountdown.daysLeft = response.data.daysLeft
    examCountdown.isSet = response.data.isSet
    examCountdown.isPast = false
    newExamDate.value = ''
    snackbar.success(t('student.progress.examDateSaved'))
  } catch (error) {
    snackbar.error(t('student.progress.examDateError'))
  }
}

const clearExamDate = async () => {
  try {
    await api.put('/student/progress/exam-countdown', { examDate: null })
    examCountdown.examDate = null
    examCountdown.daysLeft = null
    examCountdown.isSet = false
    examCountdown.isPast = false
  } catch (error) {
    snackbar.error(t('student.progress.loadError'))
  }
}

const startRecommendedTest = () => {
  const topics = weakTopics.value.map(t => t.category).filter(Boolean)
  if (topics.length > 0) {
    router.push('/student/tests?mode=TRAINING_MODE&topics=' + encodeURIComponent(topics.join(',')))
  } else {
    router.push('/student/tests?mode=TRAINING_MODE')
  }
}

const startCategoryTest = (category) => {
  if (category) {
    router.push('/student/tests?mode=TRAINING_MODE&topics=' + encodeURIComponent(category))
  } else {
    router.push('/student/tests?mode=TRAINING_MODE')
  }
}

const loadProgressRecommendations = async () => {
  try {
    const response = await api.get('/student/progress/recommendations')
    progressRecommendations.value = response.data.recommendations || []
  } catch (error) {
    progressRecommendations.value = [
      {
        id: 1,
        title: t('student.progress.rec1Title'),
        description: t('student.progress.rec1Desc'),
        icon: 'mdi-traffic-light',
        color: 'warning',
        action: t('student.progress.rec1Action'),
        type: 'test',
        category: t('student.progress.cat_road_signs')
      },
      {
        id: 2,
        title: t('student.progress.rec2Title'),
        description: t('student.progress.rec2Desc'),
        icon: 'mdi-calendar-plus',
        color: 'primary',
        action: t('student.progress.rec2Action'),
        type: 'lesson'
      },
      {
        id: 3,
        title: t('student.progress.rec3Title'),
        description: t('student.progress.rec3Desc'),
        icon: 'mdi-clock',
        color: 'info',
        action: t('student.progress.rec3Action'),
        type: 'schedule'
      }
    ]
  }
}

const loadProgressGoals = async () => {
  try {
    const response = await api.get('/student/progress/goals')
    progressGoals.value = response.data.goals || []
  } catch (error) {
    progressGoals.value = [
      {
        id: 1,
        title: t('student.progress.goal1Title'),
        description: t('student.progress.goal1Desc'),
        progress: 75,
        color: 'primary'
      },
      {
        id: 2,
        title: t('student.progress.goal2Title'),
        description: t('student.progress.goal2Desc'),
        progress: 60,
        color: 'success'
      },
      {
        id: 3,
        title: t('student.progress.goal3Title'),
        description: t('student.progress.goal3Desc'),
        progress: 45,
        color: 'warning'
      }
    ]
  }
}

const loadMockStatistics = () => {
  progressStats.testsCompleted = 32
  progressStats.averageTestScore = 87
  progressStats.lessonsCompleted = 13
  progressStats.totalHours = 19.5
  progressStats.studyTime = `24${t('student.progress.timeHours')}`
  progressStats.achievementsCount = 2
  
  progressRecentTests.value = [
    { id: 1, date: new Date('2025-10-25'), score: 92 },
    { id: 2, date: new Date('2025-10-23'), score: 85 },
    { id: 3, date: new Date('2025-10-20'), score: 78 },
    { id: 4, date: new Date('2025-10-18'), score: 90 },
    { id: 5, date: new Date('2025-10-15'), score: 88 }
  ]
  
  progressRecentLessons.value = [
    { id: 1, date: new Date('2025-10-26'), timeSlot: '14:00-15:30', status: 'COMPLETED' },
    { id: 2, date: new Date('2025-10-24'), timeSlot: '16:00-17:30', status: 'COMPLETED' },
    { id: 3, date: new Date('2025-10-22'), timeSlot: '10:00-11:30', status: 'COMPLETED' },
    { id: 4, date: new Date('2025-10-19'), timeSlot: '14:00-15:30', status: 'COMPLETED' },
    { id: 5, date: new Date('2025-10-17'), timeSlot: '16:00-17:30', status: 'COMPLETED' }
  ]
}

// ==========================================
// Progress Helpers
// ==========================================
const categoryNames = computed(() => ({
  general: t('student.progress.cat_general'),
  traffic_rules: t('student.progress.cat_traffic_rules'),
  road_signs: t('student.progress.cat_road_signs'),
  road_marking: t('student.progress.cat_road_marking'),
  traffic_signals: t('student.progress.cat_traffic_signals'),
  speed_limits: t('student.progress.cat_speed_limits'),
  overtaking: t('student.progress.cat_overtaking'),
  parking: t('student.progress.cat_parking'),
  intersections: t('student.progress.cat_intersections'),
  first_aid: t('student.progress.cat_first_aid'),
  safety: t('student.progress.cat_safety'),
  technical: t('student.progress.cat_technical')
}))

const formatCategoryName = (cat) => {
  return categoryNames.value[cat] || cat
}

const formatStudyTime = (minutes) => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours > 0) {
    return `${hours}${t('student.progress.timeHours')} ${mins}${t('student.progress.timeMinutes')}`
  }
  return `${mins}${t('student.progress.timeMinutes')}`
}

const formatFullDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('kk-KZ', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatProgressDate = (date) => {
  return new Date(date).toLocaleDateString('kk-KZ', {
    month: 'short',
    day: 'numeric'
  })
}

const getProgressColor = (progress) => {
  if (progress >= 80) return 'success'
  if (progress >= 60) return 'warning'
  if (progress >= 40) return 'info'
  return 'error'
}

const getProgressIcon = (progress) => {
  if (progress >= 90) return 'mdi-trophy'
  if (progress >= 70) return 'mdi-star'
  if (progress >= 50) return 'mdi-thumb-up'
  if (progress >= 30) return 'mdi-play'
  return 'mdi-clock-outline'
}

const getSkillColor = (rating) => {
  if (rating >= 4.5) return 'success'
  if (rating >= 4.0) return 'info'
  if (rating >= 3.5) return 'warning'
  return 'error'
}

const getLessonStatusColor = (status) => {
  switch (status) {
    case 'COMPLETED': return 'success'
    case 'SCHEDULED': return 'primary'
    case 'CANCELLED': return 'error'
    default: return 'grey'
  }
}

const getLessonStatusIcon = (status) => {
  switch (status) {
    case 'COMPLETED': return 'mdi-check-circle'
    case 'SCHEDULED': return 'mdi-calendar-clock'
    case 'CANCELLED': return 'mdi-cancel'
    default: return 'mdi-calendar'
  }
}

const handleRecommendation = (recommendation) => {
  switch (recommendation.type) {
    case 'test':
      router.push('/student/tests?mode=TRAINING_MODE')
      break
    case 'lesson':
      router.push('/student/schedule')
      break
    case 'schedule':
      snackbar.success(t('student.progress.scheduleInProgress'))
      break
    default:
      break
  }
}

// ==========================================
// Lifecycle
// ==========================================
onMounted(() => {
  checkRestrictionRedirect()
  loadProgressData()
})
</script>

<style scoped>
.student-dashboard {
  min-height: 100vh;
  background: #1a1a1a;
}

.welcome-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.telegram-connect-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.telegram-code-fallback {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(3, 169, 244, 0.22);
}

.telegram-code-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(3, 169, 244, 0.12);
}

.telegram-code-row code {
  color: inherit;
  font-weight: 700;
}

.telegram-connect-content .v-btn {
  flex-shrink: 0;
}

.profile-avatar {
  border: 3px solid #667eea;
}

.dark-card {
  background: #2d2d2d !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.stat-card {
  transition: transform 0.3s ease;
  cursor: pointer;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.overview-card {
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.02));
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.overview-metric {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 14px;
}

.overview-metric.compact {
  padding: 12px;
  height: 100%;
}

.overview-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: 8px;
}

.overview-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.72);
  margin-top: 4px;
}

.compact-panel,
.minimal-section {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 16px;
}

.section-label {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 12px;
}

.cursor-pointer {
  cursor: pointer;
}

.achievement-card {
  transition: all 0.3s ease;
  opacity: 0.6;
}

.achievement-card.unlocked {
  opacity: 1;
  transform: scale(1.02);
}

.achievement-card:hover {
  transform: translateY(-2px);
}

.achievement-card.unlocked:hover {
  transform: translateY(-2px) scale(1.02);
}

.bg-primary-lighten-5 {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.ranking-table {
  --ranking-card-height: 96px;
  --ranking-card-radius: 18px;
}

.ranking-table-desktop {
  padding: 10px 0 14px;
}

.ranking-table-desktop :deep(table) {
  width: 100%;
  table-layout: fixed;
}

.ranking-table-desktop :deep(th) {
  padding: 14px 12px;
  font-size: 0.92rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
}

.ranking-table-desktop :deep(td) {
  padding: 10px 12px;
  vertical-align: middle;
}

.ranking-col-rank {
  width: 72px;
}

.ranking-col-student {
  width: 29%;
}

.ranking-col-accuracy {
  width: 26%;
}

.ranking-col-progress {
  width: 35%;
}

.ranking-rank-cell {
  width: 72px;
}

.ranking-rank-badge {
  width: 48px;
  height: 48px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.03));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  font-size: 1rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.92);
}

.ranking-rank-badge.is-self {
  border-color: rgba(25, 118, 210, 0.24);
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.16), rgba(255, 255, 255, 0.02));
}

.ranking-student-card,
.ranking-metric-card,
.ranking-progress-card {
  width: 100%;
  min-height: var(--ranking-card-height);
  margin: 0 auto;
  padding: 12px 14px;
  border-radius: var(--ranking-card-radius);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.ranking-table-desktop .ranking-student-card,
.ranking-table-desktop .ranking-metric-card,
.ranking-table-desktop .ranking-progress-card {
  max-width: none;
}

.ranking-student-card {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
}

.ranking-metric-card,
.ranking-progress-card {
  display: flex;
  flex-direction: column;
}

.ranking-student-card.is-self,
.ranking-metric-card.is-self,
.ranking-progress-card.is-self {
  border-color: rgba(25, 118, 210, 0.24);
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.14), rgba(255, 255, 255, 0.02));
}

.ranking-student-name {
  width: 100%;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.26;
  color: rgba(255, 255, 255, 0.95);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.ranking-card-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.58);
}

.ranking-metric-header,
.ranking-progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.ranking-progress-count {
  font-size: 1rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.97);
  line-height: 1.15;
  white-space: nowrap;
}

.ranking-progress-percent {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.ranking-progress-percent.is-low {
  background: rgba(251, 191, 36, 0.18);
  color: #fde68a;
}

.ranking-progress-percent.is-mid {
  background: rgba(163, 230, 53, 0.18);
  color: #d9f99d;
}

.ranking-progress-percent.is-high {
  background: rgba(34, 197, 94, 0.2);
  color: #bbf7d0;
}

.ranking-metric-badge {
  min-width: 56px;
}

.ranking-progress-track {
  position: relative;
  overflow: hidden;
  height: 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.35);
}

.ranking-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #fbbf24 0%, #facc15 36%, #a3e635 68%, #22c55e 100%);
  box-shadow: 0 0 16px rgba(250, 204, 21, 0.28);
  transition: width 0.35s ease;
}

.ranking-progress-track.is-accuracy {
  height: 10px;
  margin-top: auto;
}

.ranking-progress-fill.is-accuracy {
  background: linear-gradient(90deg, #f97316 0%, #facc15 40%, #84cc16 72%, #22c55e 100%);
  box-shadow: 0 0 14px rgba(249, 115, 22, 0.22);
}

.ranking-progress-footer {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ranking-progress-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-width: 0;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: rgba(255, 255, 255, 0.84);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  white-space: nowrap;
}

.ranking-progress-pill.is-rank {
  flex: 0 0 auto;
  min-width: 64px;
  color: #fde68a;
  background: rgba(251, 191, 36, 0.14);
  border-color: rgba(251, 191, 36, 0.18);
}

.ranking-separator-label {
  padding: 6px 0;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.54);
}

.ranking-mobile-list {
  padding: 14px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ranking-mobile-item {
  padding: 10px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.ranking-mobile-item.is-self {
  background: rgba(25, 118, 210, 0.1);
  border-color: rgba(25, 118, 210, 0.24);
}

.ranking-mobile-head {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 10px;
  align-items: stretch;
}

.ranking-mobile-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.ranking-mobile-item .ranking-student-card,
.ranking-mobile-item .ranking-metric-card,
.ranking-mobile-item .ranking-progress-card {
  max-width: none;
  min-height: 0;
  margin: 0;
}

.ranking-student-card-mobile {
  min-height: 70px;
}

.ranking-metric-card-mobile {
  min-height: 86px;
}

.ranking-progress-card-mobile {
  min-height: 118px;
}

.ranking-mobile-item .ranking-progress-count {
  margin-bottom: 10px;
  font-size: 1.02rem;
}

.ranking-mobile-divider {
  padding: 2px 4px 0;
  text-align: center;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.54);
}

.next-lesson-card {
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 12px;
}

.progress-item {
  transition: transform 0.2s ease;
}

.progress-item:hover {
  transform: translateX(4px);
}

.chart-container {
  min-height: 120px;
}

/* Mobile Optimizations */
@media (max-width: 960px) {
  .student-dashboard {
    background: #1a1a1a;
  }

  .overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  
  .v-container {
    padding: 2px !important;
  }
  
  .stat-card .v-card-text {
    padding: 2px !important;
  }
  
  .v-card {
    margin-bottom: 4px !important;
  }
  
  .v-card-title {
    padding: 4px 8px !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
  }
  
  .v-card-text {
    padding: 4px !important;
  }
  
  .v-row {
    margin: 0 !important;
  }
  
  .v-col {
    padding: 1px !important;
  }
  
  .stat-card:hover {
    transform: none;
  }
  
  .chart-container {
    min-height: 80px;
  }

  .ranking-mobile-list {
    padding: 10px;
    gap: 10px;
  }

  .ranking-mobile-item {
    padding: 9px;
  }

  .ranking-card-label {
    font-size: 0.62rem;
  }

  .ranking-student-name {
    font-size: 0.92rem;
  }

  .ranking-progress-count {
    font-size: 0.96rem;
  }
}

@media (max-width: 600px) {
  .telegram-connect-content {
    align-items: stretch;
    flex-direction: column;
  }

  .overview-grid {
    grid-template-columns: 1fr;
  }

  .overview-value {
    font-size: 1.2rem;
  }

  .v-container {
    padding: 1px !important;
  }
  
  .stat-card .v-card-text {
    padding: 1px !important;
  }
  
  .v-card-title {
    padding: 3px 6px !important;
    font-size: 0.75rem !important;
  }

  .ranking-mobile-head {
    grid-template-columns: 46px minmax(0, 1fr);
    gap: 8px;
  }

  .ranking-rank-badge {
    width: 44px;
    height: 44px;
    border-radius: 14px;
  }

  .ranking-mobile-item {
    padding: 8px;
    border-radius: 18px;
  }

  .ranking-student-card,
  .ranking-metric-card,
  .ranking-progress-card {
    padding: 10px 11px;
  }

  .ranking-student-card-mobile {
    min-height: 64px;
  }

  .ranking-metric-card-mobile {
    min-height: 82px;
  }

  .ranking-progress-card-mobile {
    min-height: 108px;
  }

  .ranking-progress-percent {
    min-width: 42px;
    padding: 3px 8px;
    font-size: 0.72rem;
  }

  .ranking-progress-pill {
    font-size: 0.66rem;
    padding: 4px 6px;
  }

  .ranking-progress-pill.is-rank {
    min-width: 52px;
  }

  .ranking-student-name {
    font-size: 0.86rem;
  }
}
</style>



